import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import { TextField, Button, MenuItem, Select, FormControl, InputLabel, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, Menu, InputAdornment } from "@mui/material";
import { FixedSizeList } from "react-window";
import { useTranslation } from "react-i18next";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import ItemSelectorV2 from "../../../utils/Selector/ItemSelectorV2";
import ModernCalculationSelector from "../../../utils/Selector/ModernCalculationSelector";
import { toast } from "react-hot-toast";
import {
  PlusCircleIcon,
  TrashIcon,
  PlusIcon,
  PencilSquareIcon,
  FolderIcon as FolderOutlineIcon,
  Cog6ToothIcon,
  ClockIcon,
  CubeIcon,
  DocumentTextIcon,
  CalculatorIcon,
  ChatBubbleBottomCenterTextIcon,
} from "@heroicons/react/24/outline";
import { FolderIcon } from "@heroicons/react/24/solid";
import ProConfirmation from "../../../utils/ProConfirmation";
import { BulkBar } from "../../../utils/BulkBar";
import {
  buildGroupedDisplayRows,
  LINE_DROP_MIME,
  normalizeOfferGroupId,
  readOfferGroupId,
  reorderCalcItems,
  shouldShowGroupHierarchy,
  UNGROUPED_KEY,
} from "../utils/offer-calc-item-groups";
import {
  adoptPristineServerGridRows,
  calcRowIdentity,
  mergeIncomingCalcRows,
  pendingDeleteKeysFor,
  sameRowOrder,
} from "../utils/offer-calc-row-sync";
import {
  assignDisplayNumbers,
  flattenGroups,
  formatGroupLabel,
  groupDisplayTitle,
} from "../utils/offer-group-hierarchy";
import { useSettings } from "../../../../contexts/settings-context";
import {
  ACTIVE_HOUR_RATES_PARAMS,
  getHourRatesAsync,
} from "../../../../slices/offer-calculation-hour-rate";
import { useDispatch, useSelector } from "react-redux";
import { PromangRichTextEditor } from "../../../utils/PromangRichTextEditor";
import { PromangRichTextContent } from "../../../utils/PromangRichTextContent";
import { stripHtml } from "../../../Utils";
import "../../../utils/PromangRichTextEditor.css";

/** Numeric line-editor inputs — select-all on focus so typing replaces the value. */
const CALC_NUMERIC_INPUT_NAMES = new Set([
  "quantity",
  "sales_price",
  "purchase_price",
  "discount",
  "surcharge",
  "hours",
  "hour_rate",
  "cost_hour_rate",
]);

function isCalcNumericInput(el) {
  if (!el || el.tagName !== "INPUT") return false;
  if (el.disabled || el.readOnly) return false;
  const type = String(el.type || "text").toLowerCase();
  if (
    type === "checkbox" ||
    type === "radio" ||
    type === "hidden" ||
    type === "button" ||
    type === "file" ||
    type === "search"
  ) {
    return false;
  }
  if (type === "number") return true;
  const mode = String(el.inputMode || "").toLowerCase();
  if (mode === "decimal" || mode === "numeric") return true;
  const name = el.getAttribute("name") || "";
  return CALC_NUMERIC_INPUT_NAMES.has(name);
}

function selectCalcNumericFieldOnFocus(event) {
  const el = event.target;
  if (!isCalcNumericInput(el)) return;
  el.dataset.calcSelectAll = "1";
  requestAnimationFrame(() => {
    try {
      el.select();
    } catch (_err) {
      /* type=number in some browsers */
    }
  });
}

function keepCalcNumericSelectionOnMouseUp(event) {
  const el = event.target;
  if (!isCalcNumericInput(el)) return;
  if (el.dataset.calcSelectAll !== "1") return;
  event.preventDefault();
  delete el.dataset.calcSelectAll;
  try {
    el.select();
  } catch (_err) {
    /* type=number in some browsers */
  }
}

/** Quill empty / whitespace-only HTML → treat as blank. */
function isBlankNoteHtml(html) {
  return !stripHtml(html || "").trim();
}

function noteBodyHtml(note) {
  return note?.text || note?.description || "";
}

/** Additive time on product lines — unit = product_price + hours×rate. */
const PRODUCT_PLUS_HOURS_MODES = new Set([
  "product_plus_hours",
  "additive",
  "hours_in_addition",
]);

function readHoursPricingMode(item) {
  return (
    item?.hours_pricing_mode ||
    item?.custom_fields?.hours_pricing_mode ||
    null
  );
}

function isProductPlusHoursMode(item) {
  // product_plus_hours is only valid on product lines — never on pure hours.
  if (!item || item.line_type === "hours") return false;
  const mode = String(readHoursPricingMode(item) || "");
  if (PRODUCT_PLUS_HOURS_MODES.has(mode)) return true;
  if (mode === "from_price" || mode === "hours_only") return false;
  if (item.line_type !== "product") return false;
  const hours = parseLooseNumber(item?.hours);
  const rate = parseLooseNumber(item?.hour_rate);
  return hours > 0 && rate > 0;
}

function parseLooseNumber(value) {
  if (value === "" || value == null) return 0;
  const cleaned = String(value).replace(/\s/g, "").replace(",", ".");
  return parseFloat(cleaned) || 0;
}

/** Additive time UI only on product lines (never on hours / manual / calculation). */
function supportsProductPlusHoursUi(item) {
  return item?.line_type === "product";
}

function patchHoursPricingMode(row, mode) {
  const custom_fields = { ...(row?.custom_fields || {}) };
  if (mode) {
    custom_fields.hours_pricing_mode = mode;
  } else {
    delete custom_fields.hours_pricing_mode;
  }
  return {
    ...row,
    hours_pricing_mode: mode || null,
    custom_fields,
    ...(mode === "product_plus_hours"
      ? { calculate_hours_from_sales: false, calculate_hours_from_cost: false }
      : {}),
  };
}

function clearHoursLineProductBinding(row) {
  const custom_fields = { ...(row?.custom_fields || {}) };
  delete custom_fields.hours_pricing_mode;
  return {
    ...row,
    product_id: null,
    product: null,
    hours_pricing_mode: null,
    custom_fields,
  };
}

/**
 * product_plus_hours stays on product lines only.
 * Legacy hours+product+rate → promote to product + additive.
 * Other hours rows: strip product_id (products/kalkyler are notes only).
 */
function healProductPlusHoursRow(row) {
  if (!row) return row;
  const hasProduct = Boolean(row.product_id || row.product?.id);
  const hours = parseLooseNumber(row.hours);
  const rate = parseLooseNumber(row.hour_rate);
  const mode = readHoursPricingMode(row);
  const additiveReady =
    hasProduct &&
    hours > 0 &&
    rate > 0 &&
    mode !== "from_price" &&
    mode !== "hours_only";

  if (row.line_type === "hours") {
    if (additiveReady) {
      let next = {
        ...row,
        line_type: "product",
        show_hours: true,
        show_image: row.show_image !== false,
        show_url: row.show_url !== false,
      };
      if (!PRODUCT_PLUS_HOURS_MODES.has(String(mode || ""))) {
        next = patchHoursPricingMode(next, "product_plus_hours");
      } else {
        next = patchHoursPricingMode(next, mode);
      }
      return next;
    }
    if (hasProduct || PRODUCT_PLUS_HOURS_MODES.has(String(mode || ""))) {
      return clearHoursLineProductBinding(row);
    }
    return row;
  }

  if (row.line_type === "product" && additiveReady && row.show_hours) {
    if (!PRODUCT_PLUS_HOURS_MODES.has(String(mode || ""))) {
      return patchHoursPricingMode(row, "product_plus_hours");
    }
  }
  return row;
}

function lineTypeIconEl(lineType, className = "h-4 w-4") {
  switch (lineType) {
    case "hours":
      return <ClockIcon className={className} aria-hidden />;
    case "product":
      return <CubeIcon className={className} aria-hidden />;
    case "calculation":
      return <CalculatorIcon className={className} aria-hidden />;
    case "note":
      return <ChatBubbleBottomCenterTextIcon className={className} aria-hidden />;
    case "manual":
    default:
      return <DocumentTextIcon className={className} aria-hidden />;
  }
}
import {
  resolveCalculationContributionMargin,
  resolveCalculationContributionMarginPercent,
  resolveCalculationCostTotal,
  resolveCalculationSalesTotal,
  resolveLineCostDisplay,
  resolveLineSalesDisplay,
  isModuleCalculation,
  parseMoneyAmount,
} from "../utils";
import { lineAmount } from "../utils/offer-workspace";
import { mergeRemoteRowMoney } from "../utils/offer-calc-row-money";
import { CalculationInfoIcon } from "../../accounting/CalculationInfoIcon";
import OfferPricingMethodDialog from "../offer-pricing-method-dialog";
import { CalculationGuideDialog } from "../../accounting/CalculationGuideDialog";
import { OfferViewportSummary } from "../offer-viewport-summary";
import NestedModuleInsertFields, {
  recommendedFieldsNeedAttention,
  summarizeRecommendedInsertFields,
} from "./nested-module-insert-fields";
import { calculationModuleHoursTotal } from "./module-graph-hours";


/**
 * offer-17: compact grid row count above which we virtualize (react-window).
 * Below this we render normally so the inline editor / focus is untouched.
 */
const OFFER_CALC_VIRTUALIZE_THRESHOLD = 40;
/** Fixed compact-row height (px) used by FixedSizeList. Matches .offer-calc-grid__row density. */
const OFFER_CALC_COMPACT_ROW_HEIGHT = 40;
const OFFER_CALC_COMFORTABLE_ROW_HEIGHT = 52;

/** Personal workspace chrome prefs (density + optional columns). Mirrors pm_tv.* localStorage pattern. */
const WORKSPACE_PREFS_KEY = "pm_offer_calc_workspace";
const DEFAULT_WORKSPACE_PREFS = {
  density: "compact",
  colRisk: false,
  colSource: false,
};

const readWorkspacePrefs = () => {
  try {
    const raw = window.localStorage.getItem(WORKSPACE_PREFS_KEY);
    if (!raw) return { ...DEFAULT_WORKSPACE_PREFS };
    const parsed = JSON.parse(raw);
    return {
      density: parsed?.density === "comfortable" ? "comfortable" : "compact",
      colRisk: Boolean(parsed?.colRisk),
      colSource: Boolean(parsed?.colSource),
    };
  } catch {
    return { ...DEFAULT_WORKSPACE_PREFS };
  }
};

const writeWorkspacePrefs = (prefs) => {
  try {
    window.localStorage.setItem(WORKSPACE_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore quota / privacy mode */
  }
};

const WORKSPACE_LINE_TYPE_LABEL_KEY = (lineType) => {
  switch (lineType) {
    case "manual":
      return "offer.calculation.workspace.line_type.manual";
    case "product":
    case "sale":
      return "offer.calculation.workspace.line_type.product";
    case "hours":
      return "offer.calculation.workspace.line_type.hours";
    case "calculation":
      return "offer.calculation.workspace.line_type.calculation";
    case "note":
      return "offer.calculation.workspace.line_type.note";
    default:
      return "offer.calculation.workspace.line_type.other";
  }
};

/** Normalize API line_type aliases for badge CSS (sale → product). */
const workspaceBadgeType = (lineType) => {
  if (lineType === "sale") return "product";
  return lineType || "other";
};

export default function OfferCalculationItems(props) {
  const {
    setFieldValue,
    values,
    isLoading,
    name,
    calculationType,
    calculationId,
    calculationV2Enabled = false,
    authoritativeCalculation = null,
    workspaceMode = false,
    selectedRowOrder = null,
    onSelectRow = null,
    onContextItemChange = null,
    selectedGroupId = null,
    hierarchyGroups = null,
    onSelectGroup = null,
    lineMoveRequest = null,
    onLineMoveApplied = null,
    onPlacementDirty = null,
    onItemsPatched = null,
    placementEpoch = 0,
    /** Bumped on explicit «Bruk server» — clear grid delete tombstones and adopt rows. */
    serverAdoptEpoch = 0,
    onUseTemplate = null,
    onLoadPageTemplate = null,
    onOpenPresentation = null,
    onAddStructure = null,
    onEditNestedCalculation = null,
    onAttachNestedTemplate = null,
    onNestedModulePersist = null,
    saveStatus = "idle",
    onLocalEditorChange = null,
  } = props;
  const onPlacementDirtyRef = useRef(onPlacementDirty);
  const onItemsPatchedRef = useRef(onItemsPatched);
  onPlacementDirtyRef.current = onPlacementDirty;
  onItemsPatchedRef.current = onItemsPatched;
  const { t } = useTranslation();
  const { priceFormatter } = useSettings();
  const dispatch = useDispatch();

  // Settings for main fields visibility
  const [showHours, setShowHours] = useState(values?.show_hours !== undefined ? values.show_hours : true);
  const [showQuantities, setShowQuantities] = useState(values?.show_quantities !== undefined ? values.show_quantities : true);
  const [showProducts, setShowProducts] = useState(values?.show_products !== undefined ? values.show_products : true);
  const [showSetupOnly, setShowSetupOnly] = useState(values?.show_setup_only !== undefined ? values.show_setup_only : false);
  const [showQuantitiesForOfferItemOnly, setShowQuantitiesForOfferItemOnly] = useState(values?.show_quantities_for_offer_item_only !== undefined ? values.show_quantities_for_offer_item_only : false);
  const [includeProductShortDescription, setIncludeProductShortDescription] = useState(values?.include_product_short_description !== undefined ? values.include_product_short_description : false);

  const rows = values?.[name] || [];

  const [productSelectorOpen, setProductSelectorOpen] = useState(false);
  const [calculationSelectorOpen, setCalculationSelectorOpen] = useState(false);
  const [selectedItemRow, setSelectedItemRow] = useState(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const deleteTargetRef = useRef(null);
  const [selectedItemForCalculation, setSelectedItemForCalculation] = useState(null);
  const [customFieldDialogOpen, setCustomFieldDialogOpen] = useState(false);
  const [editingItemForCustomField, setEditingItemForCustomField] = useState(null);
  const [editingCustomField, setEditingCustomField] = useState(null);
  const [newCustomField, setNewCustomField] = useState({
    key: "",
    label: "",
    type: "text",
    value: "",
  });
  const [selectedItemForCalculationSelect, setSelectedItemForCalculationSelect] = useState(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [selectedItemForNote, setSelectedItemForNote] = useState(null);
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  const [noteTitle, setNoteTitle] = useState("");
  const [noteText, setNoteText] = useState("");
  const [noteShowInOffer, setNoteShowInOffer] = useState(false);
  /** When true, picking a text template copies its title into the note title field. */
  const [noteUseTemplateTitle, setNoteUseTemplateTitle] = useState(true);
  const [noteTemplateId, setNoteTemplateId] = useState(null);
  const [noteTemplateTitle, setNoteTemplateTitle] = useState(null);
  /** Last picked template title in this dialog session (for toggle-on apply). */
  const [noteLastTemplateTitle, setNoteLastTemplateTitle] = useState("");
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [calculationSettingsDialogOpen, setCalculationSettingsDialogOpen] = useState(false);
  const [selectedItemForSettings, setSelectedItemForSettings] = useState(null);
  const [hourRateSelectorOpen, setHourRateSelectorOpen] = useState(false);
  const [selectedItemForHourRate, setSelectedItemForHourRate] = useState(null);
  const hourRates = useSelector(
    (store) => store.offerCalculationHourRates?.hourRates || []
  );
  const hourRatesLoading = useSelector(
    (store) => store.offerCalculationHourRates?.isLoading
  );

  // offer-17: multi-select mass actions (compact/workspace grid only).
  const [selectedRowOrders, setSelectedRowOrders] = useState(() => new Set());
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const lastSelectedIndexRef = useRef(null);

  // Claude-like workspace chrome (toolbar / column visibility / totals strip).
  // Density + optional columns are personal prefs (localStorage), not offer data.
  const [gridDensity, setGridDensity] = useState(() => readWorkspacePrefs().density);
  const [colRisiko, setColRisiko] = useState(() => readWorkspacePrefs().colRisk);
  const [colKilde, setColKilde] = useState(() => readWorkspacePrefs().colSource);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const settingsMenuRef = useRef(null);
  const [pricingDialogItem, setPricingDialogItem] = useState(null);
  const [totalsOpen, setTotalsOpen] = useState(true);
  const [summaryGuideKey, setSummaryGuideKey] = useState(null);
  const [collapsedGridGroups, setCollapsedGridGroups] = useState(() => new Set());
  const [gridBodyEl, setGridBodyEl] = useState(null);
  const [gridBodyHeight, setGridBodyHeight] = useState(0);
  const localEditorOpen = productSelectorOpen || calculationSelectorOpen || deleteConfirmationOpen || customFieldDialogOpen || noteDialogOpen || settingsDialogOpen || calculationSettingsDialogOpen || hourRateSelectorOpen || bulkDeleteConfirmOpen || !!pricingDialogItem;
  useLayoutEffect(() => {
    onLocalEditorChange?.(localEditorOpen);
    return () => onLocalEditorChange?.(false);
  }, [localEditorOpen, onLocalEditorChange]);

  useEffect(() => {
    writeWorkspacePrefs({
      density: gridDensity,
      colRisk: colRisiko,
      colSource: colKilde,
    });
  }, [gridDensity, colRisiko, colKilde]);

  useEffect(() => {
    if (!settingsMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (
        settingsMenuRef.current &&
        !settingsMenuRef.current.contains(event.target)
      ) {
        setSettingsMenuOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setSettingsMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [settingsMenuOpen]);

  useLayoutEffect(() => {
    if (!gridBodyEl || typeof ResizeObserver === "undefined") return undefined;
    const apply = () => {
      const h = Math.floor(gridBodyEl.getBoundingClientRect().height);
      if (h > 0) {
        setGridBodyHeight((prev) => (Math.abs(prev - h) < 1 ? prev : h));
      }
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(gridBodyEl);
    return () => ro.disconnect();
  }, [gridBodyEl]);

  const [moveMenu, setMoveMenu] = useState({
    anchorEl: null,
    rowOrder: null,
  });

  const resetNoteDialog = useCallback(() => {
    setNoteDialogOpen(false);
    setSelectedItemForNote(null);
    setEditingNoteIndex(null);
    setNoteTitle("");
    setNoteText("");
    setNoteShowInOffer(false);
    setNoteUseTemplateTitle(true);
    setNoteTemplateId(null);
    setNoteTemplateTitle(null);
    setNoteLastTemplateTitle("");
  }, []);

  const openNoteDialog = useCallback((item, noteIdx = null) => {
    setSelectedItemForNote(item);
    if (noteIdx != null && item?.notes?.[noteIdx]) {
      const note = item.notes[noteIdx];
      const existingTitle = note.title || "";
      setEditingNoteIndex(noteIdx);
      setNoteTitle(existingTitle);
      setNoteText(noteBodyHtml(note));
      setNoteShowInOffer(Boolean(note.show_in_offer));
      setNoteUseTemplateTitle(!existingTitle.trim());
      setNoteTemplateId(note.template_id || null);
      setNoteTemplateTitle(note.template_title || null);
      setNoteLastTemplateTitle(note.template_title || "");
    } else {
      setEditingNoteIndex(null);
      setNoteTitle("");
      setNoteText("");
      setNoteShowInOffer(false);
      setNoteUseTemplateTitle(true);
      setNoteTemplateId(null);
      setNoteTemplateTitle(null);
      setNoteLastTemplateTitle("");
    }
    setNoteDialogOpen(true);
  }, []);

  const handleNoteTemplateSelect = useCallback(
    (template) => {
      const templateTitle = (template?.title || "").trim();
      if (template?.id != null) {
        setNoteTemplateId(template.id);
      }
      if (templateTitle) {
        setNoteTemplateTitle(templateTitle);
        setNoteLastTemplateTitle(templateTitle);
      }
      if (noteUseTemplateTitle && templateTitle) {
        setNoteTitle(templateTitle);
      }
    },
    [noteUseTemplateTitle]
  );

  const handleNoteUseTemplateTitleChange = useCallback(
    (checked) => {
      setNoteUseTemplateTitle(checked);
      if (checked && noteLastTemplateTitle) {
        setNoteTitle(noteLastTemplateTitle);
      }
    },
    [noteLastTemplateTitle]
  );

  // Catalog rates for "Velg timesats" — always from Redux / hour-rates endpoint.
  // Do not gate on calculationId (new calcs must still pick rates).
  const loadHourRates = useCallback(() => {
    return dispatch(getHourRatesAsync(ACTIVE_HOUR_RATES_PARAMS));
  }, [dispatch]);

  useEffect(() => {
    loadHourRates();
  }, [loadHourRates]);

  const openHourRateSelector = useCallback(
    (item) => {
      setSelectedItemForHourRate(item);
      setHourRateSelectorOpen(true);
      loadHourRates();
    },
    [loadHourRates]
  );

  const orderItems = (rows) => {
    if (!rows) return [];
    return rows.map((row, i) => {
      const healed = healProductPlusHoursRow(row);
      return {
      ...healed,
      row_order: i,
      // offer-bug-p0: preserve an already-assigned draggableId (set by
      // addItemRow for unsaved lines) instead of recomputing from index —
      // recomputing from `i` made the id-less new-line key reshuffle on every
      // reorder, which broke identity tracking in the rows<->items sync below.
      draggableId: healed.draggableId || "id" + (healed.id || i),
      // Ensure all required fields are present
      line_type: healed.line_type || "manual",
      item_type:
        healed.line_type === "note"
          ? "text"
          : healed.item_type || healed.type || calculationType || "product",
      price_mode:
        healed.line_type === "hours"
          ? "automatic"
          : healed.price_mode || "custom",
      hours: healed.hours || 0,
      hour_rate: healed.hour_rate || 0,
      cost_hour_rate: healed.cost_hour_rate || 0,
      hours_pricing_mode:
        healed.line_type === "hours"
          ? null
          : healed.hours_pricing_mode ||
            healed.custom_fields?.hours_pricing_mode ||
            null,
      calculate_hours_from_sales: healed.calculate_hours_from_sales || false,
      calculate_hours_from_cost: healed.calculate_hours_from_cost || false,
      product_id:
        healed.line_type === "hours"
          ? null
          : healed.product_id || healed.product?.id || null,
      unit_id: healed.unit_id || healed.unit?.id || null,
      product: healed.line_type === "hours" ? null : healed.product || null,
      unit: healed.unit || null,
      products: (healed.products || []).map(prod => ({
        ...prod,
        divide_by_quantity: prod.divide_by_quantity || false,
        show_image: prod.show_image !== undefined ? prod.show_image : false, // Default to false
        show_url: prod.show_url !== undefined ? prod.show_url : false, // Default to false
      })),
      product_list:
        healed.product_list ||
        healed.custom_fields?.product_list ||
        null,
      notes: healed.notes || [],
      show_hours:
        healed.line_type === "hours"
          ? true
          : healed.show_hours !== undefined
          ? healed.show_hours
          : false,
      show_image: healed.show_image !== undefined ? healed.show_image : true,
      show_url: healed.show_url !== undefined ? healed.show_url : true,
      show_note_title:
        healed.show_note_title === true ||
        healed.custom_fields?.show_note_title === true,
      note_title:
        healed.note_title ||
        healed.custom_fields?.note_title ||
        "",
      required_fields: healed.required_fields || {},
      editable_quantity: healed.editable_quantity !== undefined ? healed.editable_quantity : true,
      divide_by_quantity: healed.divide_by_quantity || false,
      nested_calculation: healed.nested_calculation ? {
        ...healed.nested_calculation,
        divide_by_quantity: healed.nested_calculation.divide_by_quantity || false,
      } : null,
      custom_fields: healed.custom_fields || {},
      // Membership in Offer::Group — persist via /offer_calculations items.
      // Canonical snake_case; accept camelCase from older/proxy responses.
      offer_group_id: readOfferGroupId(healed),
    };
    });
  };

  const defaultRows = [
    {
      id: null,
      row_order: 0,
      description: "",
      purchase_price: 0,
      sales_price: 0,
      quantity: 0,
      discount: 0,
      surcharge: 0,
      item_type: calculationType || "product",
      line_type: "manual", // "manual", "product", "calculation", "hours"
      price_mode: "custom", // "automatic" or "custom"
      hours: 0,
      hour_rate: 0,
      cost_hour_rate: 0, // Cost hour rate for worst case calculation
      hours_pricing_mode: null, // product_plus_hours | null (info / derive-from-price)
      divide_by_quantity: false, // Divide calculation by quantity
      custom_fields: {},
      notes: [], // Array of notes/templates for this line
      show_hours: false, // Whether to show hours calculation section
      required_fields: {}, // Object with field names as keys and boolean values for required status
      editable_quantity: true, // Whether quantity can be edited when calculation is used in offer
    },
  ];

  const [items, setItems] = useState(() => {
    const initialRows = values?.[name];
    // Only use defaultRows if initialRows is undefined/null (not loaded yet)
    // If initialRows is an empty array, it means calculation has 0 items, so use empty array
    let rowsToUse;
    if (initialRows !== undefined && initialRows !== null) {
      rowsToUse = initialRows; // Use the actual rows (even if empty)
    } else {
      rowsToUse = defaultRows; // Use default rows only if not loaded yet
    }
    const initialItems = orderItems(rowsToUse);
    console.log('[OfferCalculationItems] Initial state:', {
      initialRows: initialRows,
      initialRowsLength: initialRows?.length,
      rowsToUse: rowsToUse,
      rowsToUseLength: rowsToUse?.length,
      initialItems: initialItems,
      initialItemsLength: initialItems?.length,
      name: name,
      valuesName: values?.[name],
      isUndefined: initialRows === undefined,
      isNull: initialRows === null
    });
    return initialItems;
  });

  // Use refs to track previous values and prevent unnecessary updates
  const prevRowsRef = React.useRef(rows);
  const isInternalUpdateRef = React.useRef(false);
  const isInitialMountRef = React.useRef(true);
  // True after DnD / «Flytt til gruppe» / add-into-group until parent reports
  // a persisted placementEpoch that matches — Formik matching alone is NOT
  // enough (stale enableReinitialize / save merge can still arrive after).
  const placementDirtyRef = React.useRef(false);
  const placementEpochSeenRef = React.useRef(placementEpoch);
  const pendingDeleteIdsRef = React.useRef(new Set());
  const addItemLockRef = React.useRef(0);

  // offer-bug-p0: stable per-row identity for the rows<->items sync below.
  // Unsaved new lines have no `.id` yet, so comparing by `.id` alone treats
  // every unsaved line as indistinguishable (or as "the same" as any other
  // id-less row), which is what let an external rows update silently drop a
  // just-added line. draggableId is assigned once (addItemRow/orderItems)
  // and stays stable across re-renders/re-orders, so prefer it.
  const rowIdentity = (row) => calcRowIdentity(row);

  /** When placement is dirty, stamp local offer_group_id onto matching keys. */
  const preserveLocalPlacement = (localItems, incomingOrdered) => {
    if (!placementDirtyRef.current) return incomingOrdered;
    const localByKey = new Map();
    (localItems || []).forEach((item) => {
      const key = rowIdentity(item);
      if (key) localByKey.set(key, readOfferGroupId(item));
    });
    return (incomingOrdered || []).map((row) => {
      const key = rowIdentity(row);
      if (!key || !localByKey.has(key)) return row;
      return { ...row, offer_group_id: localByKey.get(key) };
    });
  };

  // If an external rows update (e.g. calculation reload after save) is
  // missing a locally-added, not-yet-persisted line, keep it instead of
  // silently wiping it — it will be reconciled once its own save round-trip
  // returns a real id.
  //
  // Critical: after a successful save the server returns the new line with a
  // real `.id`, while local still has the same line as id-less (client key).
  // Those identities never match — naïvely appending orphans duplicated the
  // line, re-triggered autosave, and created an infinite add loop. Only keep
  // orphans when the server is actually missing lines (shorter/stale payload).
  const mergeIncomingRows = (localItems, incomingRows) => {
    const ordered = preserveLocalPlacement(
      localItems,
      orderItems(incomingRows)
    );
    const merged = mergeIncomingCalcRows(
      localItems,
      ordered,
      pendingDeleteIdsRef.current
    );
    return orderItems(merged);
  };

  const findItemIndexByRowOrder = (list, rowOrder) =>
    (list || []).findIndex((row) => sameRowOrder(row?.row_order, rowOrder));

  const markPendingDeleted = (item) => {
    pendingDeleteKeysFor(item).forEach((key) => {
      pendingDeleteIdsRef.current.add(key);
    });
  };

  const patchRowNotes = (list, rowOrder, notes) => {
    const prevItems = [...(list || [])];
    const idx = findItemIndexByRowOrder(prevItems, rowOrder);
    if (idx < 0) return null;
    const current = prevItems[idx];
    prevItems[idx] = {
      ...current,
      notes,
      custom_fields: { ...(current.custom_fields || {}), notes },
    };
    return prevItems;
  };

  // Parent bumps placementEpoch after a successful PUT that includes placement.
  useEffect(() => {
    if (placementEpoch == null) return;
    if (placementEpoch === placementEpochSeenRef.current) return;
    placementEpochSeenRef.current = placementEpoch;
    placementDirtyRef.current = false;
  }, [placementEpoch]);

  // Explicit «Bruk server»: discard this-grid delete tombstones and replace
  // local items with the pristine Formik/server rows (no soft-sync strip).
  const serverAdoptEpochSeenRef = React.useRef(serverAdoptEpoch);
  useEffect(() => {
    if (serverAdoptEpoch == null) return;
    if (serverAdoptEpoch === serverAdoptEpochSeenRef.current) return;
    serverAdoptEpochSeenRef.current = serverAdoptEpoch;
    if (serverAdoptEpoch === 0) return;
    const adopted = adoptPristineServerGridRows(
      rows,
      pendingDeleteIdsRef.current
    );
    const orderedItems = orderItems(adopted);
    placementDirtyRef.current = false;
    isInternalUpdateRef.current = true;
    setItems(orderedItems);
    prevRowsRef.current = rows;
    if (setFieldValue) {
      setFieldValue(name, orderedItems);
    }
  }, [serverAdoptEpoch, rows, name, setFieldValue]);

  // Update items state when rows change from external source (e.g., when calculation is loaded)
  useEffect(() => {
    console.log('[OfferCalculationItems] useEffect rows changed:', {
      isInitialMount: isInitialMountRef.current,
      isInternalUpdate: isInternalUpdateRef.current,
      rows: rows,
      rowsLength: rows?.length,
      prevRows: prevRowsRef.current,
      prevRowsLength: prevRowsRef.current?.length,
      currentItems: items,
      currentItemsLength: items?.length
    });

    // On initial mount, always sync items with rows
    if (isInitialMountRef.current) {
      console.log('[OfferCalculationItems] Initial mount - syncing items with rows');
      isInitialMountRef.current = false;
      // Only sync if rows is defined (not undefined/null)
      // If rows is an empty array, it means calculation has 0 items, so keep empty array
      if (rows !== undefined && rows !== null) {
        const orderedItems = orderItems(rows);
        console.log('[OfferCalculationItems] Initial mount - setting items:', orderedItems);
        setItems(orderedItems);
        prevRowsRef.current = rows;
      } else {
        // rows is undefined/null - calculation not loaded yet, keep default items
        console.log('[OfferCalculationItems] Initial mount - rows is undefined/null, keeping default items');
        prevRowsRef.current = rows;
      }
      return;
    }

    // Skip if this is an internal update (from setFieldValue)
    if (isInternalUpdateRef.current) {
      console.log('[OfferCalculationItems] Skipping update - internal update flag set');
      isInternalUpdateRef.current = false;
      return;
    }

    // Compare rows by checking stable identities (id, or draggableId for
    // not-yet-persisted lines) and length
    const prevIds = prevRowsRef.current?.map(rowIdentity) || [];
    const currentIds = rows?.map(rowIdentity) || [];
    const idsChanged = JSON.stringify(prevIds) !== JSON.stringify(currentIds);
    const lengthChanged = (prevRowsRef.current?.length || 0) !== (rows?.length || 0);
    
    // Also check if rows has data but items doesn't match (safety check)
    const currentItemsIds = items?.map(rowIdentity) || [];
    const itemsDontMatchRows = rows && rows.length > 0 && (
      items.length === 0 || 
      JSON.stringify([...currentItemsIds].sort()) !== JSON.stringify([...currentIds].sort())
    );
    
    console.log('[OfferCalculationItems] Comparison results:', {
      prevIds: prevIds,
      currentIds: currentIds,
      currentItemsIds: currentItemsIds,
      idsChanged: idsChanged,
      lengthChanged: lengthChanged,
      itemsDontMatchRows: itemsDontMatchRows,
      shouldUpdate: idsChanged || lengthChanged || itemsDontMatchRows
    });
    
    if (idsChanged || lengthChanged || itemsDontMatchRows) {
      console.log('[OfferCalculationItems] Updating items from rows');
      // Only update if rows is defined (not undefined/null)
      // If rows is an empty array, it means calculation has 0 items, so set to empty array
      if (rows !== undefined && rows !== null) {
        // offer-bug-p0: merge so a not-yet-saved line survives an external
        // rows update that raced ahead of its own save (see mergeIncomingRows).
        const orderedItems = mergeIncomingRows(items, rows);
        console.log('[OfferCalculationItems] Setting items to:', orderedItems);
        setItems(orderedItems);
        prevRowsRef.current = rows;
      }
      // If rows is undefined/null, don't update (calculation not loaded yet)
    } else if (rows !== undefined && rows !== null) {
      // Identities match — reconcile offer_group_id without undoing an in-flight
      // placement edit (ungroup must be able to set null).
      // Do NOT clear placementDirty here when Formik matches — a later stale
      // save/soft-sync can still arrive; only placementEpoch clears it.
      const remoteByKey = new Map();
      rows.forEach((row) => {
        const key = rowIdentity(row);
        if (key) remoteByKey.set(key, row);
      });
      let needsWrite = false;
      const synced = (items || []).map((local) => {
        const key = rowIdentity(local);
        const remote = key ? remoteByKey.get(key) : null;
        if (!remote) return local;
        const localG = readOfferGroupId(local);
        const remoteG = readOfferGroupId(remote);
        if (localG === remoteG) {
          if (placementDirtyRef.current) return local;
          const merged = mergeRemoteRowMoney(local, remote);
          if (merged !== local) needsWrite = true;
          return merged;
        }
        if (placementDirtyRef.current) {
          // Keep local (including null after ungroup) and re-push to Formik.
          needsWrite = true;
          return { ...local, offer_group_id: localG };
        }
        // Pristine local grid: accept authoritative Formik/server placement + money.
        needsWrite = true;
        return mergeRemoteRowMoney(
          { ...local, offer_group_id: remoteG },
          remote
        );
      });
      if (needsWrite) {
        const nextItems = orderItems(synced);
        setItems(nextItems);
        if (placementDirtyRef.current) {
          queueMicrotask(() => {
            if (setFieldValue) {
              isInternalUpdateRef.current = true;
              setFieldValue(name, nextItems);
            }
          });
        }
      }
      prevRowsRef.current = rows;
    } else {
      console.log('[OfferCalculationItems] No update needed - rows and items match');
    }
  }, [rows, name, setFieldValue]);

  // Update Formik when items change (but mark as internal update to prevent loop)
  useEffect(() => {
    console.log('[OfferCalculationItems] useEffect items changed:', {
      isInitialMount: isInitialMountRef.current,
      items: items,
      itemsLength: items?.length,
      name: name,
      setFieldValue: !!setFieldValue
    });
    
    if (setFieldValue && !isInitialMountRef.current) {
      console.log('[OfferCalculationItems] Updating Formik with items:', items);
      isInternalUpdateRef.current = true;
      setFieldValue(name, items);
    }
  }, [items, name, setFieldValue]);

  // offer-17: drop selected row_orders that no longer exist (e.g. external reload).
  useEffect(() => {
    setSelectedRowOrders((prev) => {
      if (prev.size === 0) return prev;
      const valid = new Set(items.map((item) => item.row_order));
      let changed = false;
      const next = new Set();
      prev.forEach((ro) => {
        if (valid.has(ro)) next.add(ro);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [items]);

  // Sync settings from values when calculation is loaded
  useEffect(() => {
    if (values?.show_setup_only !== undefined) {
      setShowSetupOnly(values.show_setup_only);
    }
    if (values?.show_quantities_for_offer_item_only !== undefined) {
      setShowQuantitiesForOfferItemOnly(values.show_quantities_for_offer_item_only);
    }
    if (values?.include_product_short_description !== undefined) {
      setIncludeProductShortDescription(values.include_product_short_description);
    }
  }, [values?.show_setup_only, values?.show_quantities_for_offer_item_only, values?.include_product_short_description]);

  // Update parent values when settings change (removed duplicate)
  useEffect(() => {
    if (setFieldValue) {
      setFieldValue("show_hours", showHours);
      setFieldValue("show_quantities", showQuantities);
      setFieldValue("show_products", showProducts);
      setFieldValue("show_setup_only", showSetupOnly);
      setFieldValue("show_quantities_for_offer_item_only", showQuantitiesForOfferItemOnly);
      setFieldValue("include_product_short_description", includeProductShortDescription);
    }
  }, [showHours, showQuantities, showProducts, showSetupOnly, showQuantitiesForOfferItemOnly, includeProductShortDescription, setFieldValue]);

  const flatHierarchyGroups = useMemo(
    () =>
      Array.isArray(hierarchyGroups)
        ? assignDisplayNumbers(flattenGroups(hierarchyGroups))
        : [],
    [hierarchyGroups]
  );

  const showGroupHierarchy = shouldShowGroupHierarchy(flatHierarchyGroups);

  const applyOfferGroupId = useCallback(
    (rowOrder, targetGroupId) => {
      const nextGroupId = normalizeOfferGroupId(targetGroupId);
      setItems((prev) => {
        const idx = prev.findIndex(
          (item, i) =>
            Number(
              item.row_order !== undefined && item.row_order !== null
                ? item.row_order
                : i
            ) === Number(rowOrder)
        );
        if (idx < 0) return prev;
        const current = readOfferGroupId(prev[idx]);
        if (current === nextGroupId) return prev;
        placementDirtyRef.current = true;
        // Block soft-sync immediately (before microtask persist).
        onPlacementDirtyRef.current?.();
        const next = [...prev];
        next[idx] = { ...next[idx], offer_group_id: nextGroupId };
        // Persist placement immediately via parent (dirty + PUT). Do not wait
        // for Formik setFieldValue → Autosave — that debounce window is what
        // previously let soft-sync / enableReinitialize clear offer_group_id.
        queueMicrotask(() => {
          if (setFieldValue) {
            isInternalUpdateRef.current = true;
            setFieldValue(name, next);
          }
          onItemsPatchedRef.current?.(next);
        });
        return next;
      });
    },
    [name, setFieldValue]
  );

  const applyLineReorder = useCallback(
    (fromRowOrder, toRowOrder) => {
      if (fromRowOrder == null || toRowOrder == null) return;
      if (Number(fromRowOrder) === Number(toRowOrder)) return;
      setItems((prev) => {
        const indexOf = (list, rowOrder) =>
          list.findIndex(
            (item, i) =>
              Number(
                item.row_order !== undefined && item.row_order !== null
                  ? item.row_order
                  : i
              ) === Number(rowOrder)
          );
        const fromIdx = indexOf(prev, fromRowOrder);
        const toIdx = indexOf(prev, toRowOrder);
        if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return prev;
        const targetGroupId = readOfferGroupId(prev[toIdx]);
        let next = reorderCalcItems(prev, fromIdx, toIdx);
        next = next.map((item, i) =>
          i === toIdx ? { ...item, offer_group_id: targetGroupId } : item
        );
        placementDirtyRef.current = true;
        onPlacementDirtyRef.current?.();
        queueMicrotask(() => {
          if (setFieldValue) {
            isInternalUpdateRef.current = true;
            setFieldValue(name, next);
          }
          onItemsPatchedRef.current?.(next);
        });
        return next;
      });
    },
    [name, setFieldValue]
  );

  // Apply tree-drop / workspace move requests without reloading calc rows.
  // Use token so Strict Mode / unstable callback identities cannot re-apply.
  const lastLineMoveTokenRef = useRef(null);
  useEffect(() => {
    if (!lineMoveRequest || lineMoveRequest.rowOrder == null) return;
    if (
      lineMoveRequest.token != null &&
      lineMoveRequest.token === lastLineMoveTokenRef.current
    ) {
      return;
    }
    lastLineMoveTokenRef.current = lineMoveRequest.token ?? Date.now();
    applyOfferGroupId(lineMoveRequest.rowOrder, lineMoveRequest.targetGroupId);
    onLineMoveApplied?.();
  }, [lineMoveRequest, applyOfferGroupId, onLineMoveApplied]);

  const commitItems = (nextItems, { persist = false } = {}) => {
    const ordered = orderItems(nextItems);
    setItems(ordered);
    if (setFieldValue) {
      isInternalUpdateRef.current = true;
      setFieldValue(name, ordered);
    }
    if (persist) {
      onItemsPatchedRef.current?.(ordered);
    }
    return ordered;
  };

  const addItemRow = (lineType = "manual") => {
    const now = Date.now();
    if (now - addItemLockRef.current < 250) return;
    addItemLockRef.current = now;
    // Unique client key — index-based "idN" collided with orderItems defaults
    // and broke identity matching across the autosave round-trip.
    const clientKey = `client-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const inheritedGroupId = normalizeOfferGroupId(selectedGroupId);
    const isNote = lineType === "note";
    const newRow = {
      description: "",
      purchase_price: 0,
      sales_price: 0,
      quantity: lineType === "hours" ? 1 : 0,
      discount: 0,
      surcharge: 0,
      row_order: items.length,
      // NOTE lines map to customer item_type=text (PDF / Customer view).
      item_type: isNote ? "text" : calculationType || "product",
      line_type: lineType, // "manual", "product", "calculation", "hours", "note"
      // Hours = time-only with automatic pricing; product = custom unit price
      price_mode: lineType === "hours" ? "automatic" : "custom",
      hours: 0,
      hour_rate: 0,
      cost_hour_rate: 0, // Cost hour rate for worst case calculation
      hours_pricing_mode: null,
      nested_calculation_id: null,
      nested_calculation: null,
      products: [], // Array of products for this line
      notes: [], // Array of notes/templates for this line
      show_hours: lineType === "hours", // Automatically show hours section for hours lines
      show_image: lineType === "product", // Show product image in PDF
      show_url: lineType === "product", // Show product URL in PDF
      show_note_title: false, // Optional NOTE title in PDF / customer view
      note_title: "",
      required_fields: {}, // Object with field names as keys and boolean values for required status
      editable_quantity: !isNote, // Quantity N/A for note lines
      draggableId: clientKey,
      // New lines inherit the selected structure chapter when one is focused.
      offer_group_id: inheritedGroupId,
    };
    setItems((prevState) => {
      const next = [...prevState, newRow];
      if (inheritedGroupId != null) {
        placementDirtyRef.current = true;
        onPlacementDirtyRef.current?.();
      }
      queueMicrotask(() => {
        if (setFieldValue) {
          isInternalUpdateRef.current = true;
          setFieldValue(name, next);
        }
        onItemsPatchedRef.current?.(next);
      });
      return next;
    });
  };

  const toggleGridGroupCollapse = (key) => {
    setCollapsedGridGroups((prev) => {
      const next = new Set(prev);
      const k = String(key);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const openMoveMenu = (event, rowOrder) => {
    event.stopPropagation();
    setMoveMenu({ anchorEl: event.currentTarget, rowOrder });
  };

  const closeMoveMenu = () => {
    setMoveMenu({ anchorEl: null, rowOrder: null });
  };

  const handleMoveMenuSelect = (targetGroupId) => {
    const rowOrder = moveMenu.rowOrder;
    closeMoveMenu();
    if (rowOrder == null) return;
    // Apply locally first (single source of truth for grid regroup).
    // Tree bridge (onMoveLineToGroup) is only needed when the handler lives
    // outside this component — grid menu must not wait on that hop.
    applyOfferGroupId(rowOrder, targetGroupId);
  };

  // Helper function to format number with comma as decimal separator
  const formatNumber = (value) => {
    if (value === "" || value === null || value === undefined) return "0,00";
    const numValue = typeof value === "string" ? parseFloat(value.replace(",", ".")) : value;
    if (isNaN(numValue)) return "0,00";
    return numValue.toFixed(2).replace(".", ",");
  };

  // Helper function to parse formatted number back to number
  const parseFormattedNumber = (value) => {
    if (!value || value === "") return 0;
    const cleaned = String(value).replace(/\s/g, "").replace(",", ".");
    return parseFloat(cleaned) || 0;
  };

  const onItemRowChange = (e, data) => {
    let prevItems = [...items];
    const index =
      typeof data?.row_order === "number"
        ? items.findIndex(
            (row, i) =>
              row === data ||
              (row?.id != null && row.id === data.id) ||
              (row?.draggableId && row.draggableId === data.draggableId) ||
              i === data.row_order
          )
        : -1;
    const targetIndex = index >= 0 ? index : data.row_order;
    if (targetIndex == null || targetIndex < 0 || !prevItems[targetIndex]) return;
    let item = {
      ...prevItems[targetIndex],
      [e.target.name]: e.target.value,
    };
    prevItems[targetIndex] = item;
    setItems(prevItems);
  };

  const onCustomFieldChange = (fieldKey, value, item) => {
    let prevItems = [...items];
    let customFields = { ...(prevItems[item.row_order].custom_fields || {}) };
    customFields[fieldKey] = value;
    
    let updatedItem = {
      ...prevItems[item.row_order],
      custom_fields: customFields,
    };
    prevItems[item.row_order] = updatedItem;
    setItems(prevItems);
  };

  const openCustomFieldDialog = (item, fieldKey = null) => {
    setEditingItemForCustomField(item);
    if (fieldKey) {
      // Editing existing field
      const customFields = item.custom_fields || {};
      setEditingCustomField(fieldKey);
      setNewCustomField({
        key: fieldKey,
        label: fieldKey.charAt(0).toUpperCase() + fieldKey.slice(1).replace(/_/g, " "),
        type: typeof customFields[fieldKey] === "number" ? "number" : "text",
        value: customFields[fieldKey] || "",
      });
    } else {
      // Adding new field
      setEditingCustomField(null);
      setNewCustomField({
        key: "",
        label: "",
        type: "number",
        value: "",
      });
    }
    setCustomFieldDialogOpen(true);
  };

  const handleSaveCustomField = () => {
    if (!editingItemForCustomField || !newCustomField.key || !newCustomField.label) {
      toast.error("Fyll ut alle felt");
      return;
    }

    let prevItems = [...items];
    let item = prevItems.find((i) => i.row_order === editingItemForCustomField.row_order);
    if (!item) return;

    let customFields = { ...(item.custom_fields || {}) };
    
    // If editing, remove old key first if it changed
    if (editingCustomField && editingCustomField !== newCustomField.key) {
      delete customFields[editingCustomField];
    }

    // Convert value based on type
    let value = newCustomField.value;
    if (newCustomField.type === "number") {
      value = parseFloat(value) || 0;
    }

    customFields[newCustomField.key] = value;

    let updatedItem = {
      ...item,
      custom_fields: customFields,
    };
    
    prevItems[item.row_order] = updatedItem;
    setItems(prevItems);
    setCustomFieldDialogOpen(false);
    setEditingCustomField(null);
    setEditingItemForCustomField(null);
  };

  const handleDeleteCustomField = (item, fieldKey) => {
    let prevItems = [...items];
    let customFields = { ...(prevItems[item.row_order].custom_fields || {}) };
    delete customFields[fieldKey];
    
    let updatedItem = {
      ...prevItems[item.row_order],
      custom_fields: customFields,
    };
    prevItems[item.row_order] = updatedItem;
    setItems(prevItems);
  };

  const renderCustomFields = (item) => {
    const customFields = item.custom_fields || {};
    const fieldKeys = Object.keys(customFields);

    if (fieldKeys.length === 0) {
      return (
        <div className="col-span-12">
          <div className="text-sm text-gray-500 mb-2">Ingen custom fields</div>
          <Button
            size="small"
            variant="outlined"
            onClick={() => openCustomFieldDialog(item)}
            startIcon={<PlusIcon className="h-4 w-4" />}
          >
            Legg til felt
          </Button>
        </div>
      );
    }

    return (
      <div className="col-span-12">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-medium text-gray-700">Custom Fields:</div>
          <Button
            size="small"
            variant="outlined"
            onClick={() => openCustomFieldDialog(item)}
            startIcon={<PlusIcon className="h-4 w-4" />}
          >
            Legg til felt
          </Button>
        </div>
        <div className="grid grid-cols-12 gap-2">
          {fieldKeys.map((key) => (
            <div key={key} className="col-span-12 sm:col-span-4 flex items-center gap-2">
              <TextField
                label={key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ")}
                variant="standard"
                type={typeof customFields[key] === "number" ? "number" : "text"}
                value={customFields[key] || ""}
                onChange={(e) => onCustomFieldChange(key, e.target.value, item)}
                fullWidth
                size="small"
              />
              <IconButton
                size="small"
                onClick={() => openCustomFieldDialog(item, key)}
                className="text-blue-500"
              >
                <PencilSquareIcon className="h-4 w-4" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDeleteCustomField(item, key)}
                className="text-red-500"
              >
                <TrashIcon className="h-4 w-4" />
              </IconButton>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Calculate automatic sales price based on nested calculation and products
  const calculateAutomaticSalesPrice = (item) => {
    let total = 0;

    // Add nested calculation total if exists
    if (item.nested_calculation?.total_amount) {
      let nestedAmount = parseFloat(item.nested_calculation.total_amount || 0);
      // If divide_by_quantity is true on the nested calculation itself, divide by item quantity
      if (item.nested_calculation.divide_by_quantity && item.quantity && parseFloat(item.quantity) > 0) {
        nestedAmount = nestedAmount / parseFloat(item.quantity);
      }
      // If divide_by_quantity is true on the item level, also divide by quantity
      else if (item.divide_by_quantity && item.quantity && parseFloat(item.quantity) > 0) {
        nestedAmount = nestedAmount / parseFloat(item.quantity);
      }
      total += nestedAmount;
    }

    // Add products totals if exists
    if (item.products && Array.isArray(item.products)) {
      item.products.forEach((prod) => {
        const prodQuantity = parseFloat(prod.quantity || 1);
        let prodSalesPrice = parseFloat(prod.sales_price || 0);
        const prodDiscount = parseFloat(prod.discount || 0);
        const prodSurcharge = parseFloat(prod.surcharge || 0);
        
        // Only divide products if product-level divide_by_quantity is set
        // Item-level divide_by_quantity only affects hours and nested calculations, not products
        if (prod.divide_by_quantity && item.quantity && parseFloat(item.quantity) > 0) {
          prodSalesPrice = prodSalesPrice / parseFloat(item.quantity);
        }
        
        if (prodSalesPrice > 0 && prodQuantity > 0) {
          let prodTotal = prodSalesPrice * prodQuantity;
          if (prodSurcharge > 0) prodTotal += (prodTotal * prodSurcharge) / 100;
          if (prodDiscount > 0) prodTotal -= (prodTotal * prodDiscount) / 100;
          total += prodTotal;
        }
      });
    }

    // Add hours calculation if exists
    let hours = parseFormattedNumber(item.hours || 0);
    const hourRate = parseFormattedNumber(item.hour_rate || 0);
    const quantity = parseFormattedNumber(item.quantity || 1);
    // If divide_by_quantity is true, divide hours by quantity
    if (item.divide_by_quantity && item.quantity && parseFloat(item.quantity) > 0) {
      hours = hours / parseFloat(item.quantity);
    }
    // Multiply hours by quantity for total calculation (hours per unit * quantity = total hours)
    if (hours > 0 && hourRate > 0) {
      total += hours * hourRate * quantity;
    }

    return total;
  };

  // Calculate automatic purchase price based on nested calculation, products, and hours cost
  const calculateAutomaticPurchasePrice = (item) => {
    let total = 0;

    // Add hours cost calculation if exists
    // Calculate cost as: cost_hour_rate * hours * quantity (same logic as sales price)
    let hours = parseFormattedNumber(item.hours || 0);
    const costHourRate = parseFormattedNumber(item.cost_hour_rate || 0);
    const quantity = parseFormattedNumber(item.quantity || 1);
    
    // If divide_by_quantity is true, divide hours by quantity
    if (item.divide_by_quantity && item.quantity && parseFloat(item.quantity) > 0) {
      hours = hours / parseFloat(item.quantity);
    }
    
    if (hours > 0 && costHourRate > 0) {
      // Calculate cost hours total: hours * cost_hour_rate * quantity
      // Same logic as sales price: hour_rate * hours * quantity
      total += hours * costHourRate * quantity;
    }

    // Add nested calculation purchase amount if exists
    if (item.nested_calculation?.purchase_amount !== undefined) {
      let nestedCost = parseFloat(item.nested_calculation.purchase_amount || 0);
      // If divide_by_quantity is true on the nested calculation itself, divide by item quantity
      if (item.nested_calculation.divide_by_quantity && item.quantity && parseFloat(item.quantity) > 0) {
        nestedCost = nestedCost / parseFloat(item.quantity);
      }
      // If divide_by_quantity is true on the item level, also divide by quantity
      else if (item.divide_by_quantity && item.quantity && parseFloat(item.quantity) > 0) {
        nestedCost = nestedCost / parseFloat(item.quantity);
      }
      total += nestedCost;
    }

    // Add products purchase prices if exists
    if (item.products && Array.isArray(item.products)) {
      item.products.forEach((prod) => {
        const prodQuantity = parseFloat(prod.quantity || 1);
        let prodPurchasePrice = parseFloat(prod.purchase_price || 0);
        
        // Only divide products if product-level divide_by_quantity is set
        // Item-level divide_by_quantity only affects hours and nested calculations, not products
        if (prod.divide_by_quantity && item.quantity && parseFloat(item.quantity) > 0) {
          prodPurchasePrice = prodPurchasePrice / parseFloat(item.quantity);
        }
        
        if (prodPurchasePrice > 0 && prodQuantity > 0) {
          total += prodPurchasePrice * prodQuantity;
        }
      });
    }

    return total;
  };

  const calculateRowTotalPrice = (item) => {
    let total = 0;
    const quantity = parseFormattedNumber(item.quantity || 0);
    const surcharge = parseFormattedNumber(item.surcharge || 0);
    const discount = parseFormattedNumber(item.discount || 0);

    // Use automatic or custom sales price
    let salesPrice = 0;
    if (item.price_mode === "automatic") {
      salesPrice = calculateAutomaticSalesPrice(item);
    } else {
      salesPrice = parseFormattedNumber(item.sales_price || 0);
      // product_plus_hours: unit = product sales + hours × hour_rate
      if (isProductPlusHoursMode(item)) {
        const hours = parseFormattedNumber(item.hours || 0);
        const hourRate = parseFormattedNumber(item.hour_rate || 0);
        if (hours > 0 && hourRate > 0) {
          salesPrice += hours * hourRate;
        }
      }
    }

    if (salesPrice > 0 && quantity > 0) {
      total = salesPrice * quantity;
      if (surcharge > 0) total += (total * surcharge) / 100;
      if (discount > 0) total -= (total * discount) / 100;
    }

    return total;
  };

  /**
   * Local line cost mirror of API product_plus_hours / custom unit math.
   * Used to refresh v2 cost_total before the save round-trip.
   */
  const calculateRowTotalCost = (item) => {
    let total = 0;
    const quantity = parseFormattedNumber(item.quantity || 0);
    const surcharge = parseFormattedNumber(item.surcharge || 0);
    const discount = parseFormattedNumber(item.discount || 0);

    if (item.price_mode === "automatic") {
      total = calculateAutomaticPurchasePrice(item);
      if (total > 0) {
        if (surcharge > 0) total += (total * surcharge) / 100;
        if (discount > 0) total -= (total * discount) / 100;
      }
      return total;
    }

    let unitCost = parseFormattedNumber(item.purchase_price || 0);
    // product_plus_hours: unit = product cost + hours × cost_hour_rate
    if (isProductPlusHoursMode(item)) {
      const hours = parseFormattedNumber(item.hours || 0);
      const costHourRate = parseFormattedNumber(item.cost_hour_rate || 0);
      if (hours > 0 && costHourRate > 0) {
        unitCost += hours * costHourRate;
      }
    }

    if (unitCost > 0 && quantity > 0) {
      total = unitCost * quantity;
      if (surcharge > 0) total += (total * surcharge) / 100;
      if (discount > 0) total -= (total * discount) / 100;
    }

    return total;
  };

  /**
   * v2 display prefers item.line_total / cost_total from the API.
   * When hours_pricing_mode changes, refresh those caches so totals update
   * immediately — same formulas as save (product_plus_hours additive math).
   */
  const withLocalLineMoneyCaches = (item) => {
    const nestedId =
      item?.nested_calculation_id ?? item?.nested_calculation?.id;
    const nestedUnit = item?.nested_calculation?.total_amount;
    if (nestedId != null && nestedUnit != null && nestedUnit !== "") {
      const qty = parseFormattedNumber(item?.quantity || 0);
      const effectiveQty = qty > 0 ? qty : 1;
      const unit = parseFormattedNumber(nestedUnit);
      const priced = {
        ...item,
        sales_price: unit,
        quantity: effectiveQty,
      };
      return {
        ...item,
        sales_price: unit,
        line_total: lineAmount(priced),
        cost_total: calculateRowTotalCost(item),
      };
    }
    return {
      ...item,
      line_total: calculateRowTotalPrice(item),
      cost_total: calculateRowTotalCost(item),
    };
  };

  /** After local money-field edits, refresh line_total/cost_total caches. */
  const commitItemRow = (prevItems, rowOrder) => {
    const row = prevItems[rowOrder];
    if (row) {
      prevItems[rowOrder] = withLocalLineMoneyCaches(row);
    }
    setItems(prevItems);
  };

  const calculateTotalPrice = (items) => {
    let total = 0;
    if (items.length > 0) {
      items.forEach((item) => {
        total += calculateRowTotalPrice(item);
      });
    }
    return total;
  };

  /** Display helpers: v2 prefers API caches; live FE math when caches are stale. */
  const getRowSalesDisplay = (item) =>
    resolveLineSalesDisplay(item, {
      v2: calculationV2Enabled,
      legacyAmount: calculateRowTotalPrice(item),
    });

  const getFooterSalesDisplay = () => {
    const lineSum = (items || []).reduce((sum, item) => {
      const amount = getRowSalesDisplay(item)?.amount;
      return sum + (Number(amount) || 0);
    }, 0);
    if (calculationV2Enabled) {
      const cached = resolveCalculationSalesTotal(authoritativeCalculation, {
        v2: true,
      });
      const matches =
        cached != null &&
        Number.isFinite(lineSum) &&
        Math.abs(cached - lineSum) <= 0.009;
      return {
        amount: lineSum,
        authoritative: matches,
        estimate: !matches,
      };
    }
    return {
      amount: calculateTotalPrice(items),
      authoritative: false,
      estimate: false,
    };
  };

  /** Compact-grid display helpers (workspace mode). Read-only; reuse existing math. */
  const resolveUnitLabel = (item) => {
    const unit = item?.unit || item?.product?.unit;
    if (!unit) return "";
    return (
      unit.short_name ||
      unit.name ||
      unit.title ||
      unit.symbol ||
      unit.abbreviation ||
      ""
    );
  };

  const getRowUnitCost = (item) => {
    if (item?.price_mode === "automatic") return calculateAutomaticPurchasePrice(item);
    let unit = parseFormattedNumber(item?.purchase_price || 0);
    if (isProductPlusHoursMode(item)) {
      const hours = parseFormattedNumber(item.hours || 0);
      const costHourRate = parseFormattedNumber(item.cost_hour_rate || 0);
      if (hours > 0 && costHourRate > 0) unit += hours * costHourRate;
    }
    return unit;
  };

  const getRowUnitSales = (item) => {
    const nestedId =
      item?.nested_calculation_id ?? item?.nested_calculation?.id;
    const nestedUnit = parseFormattedNumber(
      item?.nested_calculation?.total_amount || 0
    );
    if (nestedId != null && nestedUnit > 0) return nestedUnit;
    if (item?.price_mode === "automatic") return calculateAutomaticSalesPrice(item);
    let unit = parseFormattedNumber(item?.sales_price || 0);
    if (isProductPlusHoursMode(item)) {
      const hours = parseFormattedNumber(item.hours || 0);
      const hourRate = parseFormattedNumber(item.hour_rate || 0);
      if (hours > 0 && hourRate > 0) unit += hours * hourRate;
    }
    return unit;
  };

  const getRowMarginPercent = (item) => {
    if (!calculationV2Enabled) return null;
    const sales = getRowSalesDisplay(item)?.amount ?? 0;
    const cost = resolveLineCostDisplay(item, { v2: true });
    if (cost == null || !(sales > 0)) return null;
    return ((sales - cost) / sales) * 100;
  };

  const lineTitle = (item, index) => {
    // Nested package: nested.title is customer/kalkyle SoT (matches Customer
    // group header). Stale parent description ("Test") must not win.
    if (item?.nested_calculation?.title) {
      return item.nested_calculation.title;
    }
    const isNote =
      item?.line_type === "note" || item?.item_type === "text";
    if (isNote) {
      const noteTitle = (item?.note_title || "").trim();
      if (item?.show_note_title === true && noteTitle) return noteTitle;
      const body = stripHtml(item?.description || "").trim();
      if (body) return body;
      return t("offer.calculation.workspace.untitled_line", { n: index + 1 });
    }
    const description = (item?.description || "").trim();
    if (description) return description;
    if (item?.product?.displayTitle) return item.product.displayTitle;
    return t("offer.calculation.workspace.untitled_line", { n: index + 1 });
  };

  /** Authoritative-first totals for the sticky totals bar. v2 prefers server caches / summary. */
  const getWorkspaceTotals = () => {
    const salesDisp = getFooterSalesDisplay();
    const sales = salesDisp.amount || 0;
    const apiSummary = authoritativeCalculation?.summary;

    // Partial breakdown from line types — show only what we can derive; never invent.
    let materialCost = 0;
    let laborCost = 0;
    let moduleCost = 0;
    let hoursSum = 0;
    let hasMaterial = false;
    let hasLabor = false;
    let hasModule = false;
    let hasHours = false;
    const missingCostRows = [];

    (items || []).forEach((it, index) => {
      const qty = parseFormattedNumber(it?.quantity || 0) || 0;
      const unitCost = getRowUnitCost(it) || 0;
      // offer-bug-p0: resolveLineCostDisplay already falls back to a kostpris
      // estimate (purchase_price/cost_hour_rate) when cost_total is 0/stale,
      // so material/labor/module sticky totals stop dropping to empty.
      const lineCost = calculationV2Enabled
        ? resolveLineCostDisplay(it, { v2: true })
        : qty > 0 && unitCost > 0
        ? unitCost * qty
        : unitCost > 0
        ? unitCost
        : null;
      const hoursField = parseFormattedNumber(it?.hours || 0) || 0;
      const qtyAsHours =
        it?.line_type === "hours" && !(hoursField > 0)
          ? parseFormattedNumber(it?.quantity || 0) || 0
          : 0;
      const hours = hoursField > 0 ? hoursField : qtyAsHours;
      if (hours > 0) {
        hoursSum += hoursField > 0 ? hours * (qty > 0 ? qty : 1) : hours;
        hasHours = true;
      }

      const type = it?.line_type || "other";
      if (type === "hours") {
        if (lineCost != null && lineCost > 0) {
          laborCost += lineCost;
          hasLabor = true;
        }
      } else if (type === "calculation") {
        if (lineCost != null && lineCost > 0) {
          moduleCost += lineCost;
          hasModule = true;
        }
      } else if (type === "product" || type === "manual") {
        if (lineCost != null && lineCost > 0) {
          materialCost += lineCost;
          hasMaterial = true;
        }
      }

      const salesDispRow = getRowSalesDisplay(it);
      const hasSales =
        it?.line_type !== "note" &&
        ((salesDispRow?.amount != null && salesDispRow.amount > 0) ||
          (parseFormattedNumber(it?.sales_price || 0) || 0) > 0);
      const missingCost =
        hasSales &&
        (lineCost == null || !(lineCost > 0)) &&
        !(unitCost > 0);
      if (missingCost) {
        const rowOrder =
          it.row_order !== undefined && it.row_order !== null
            ? it.row_order
            : index;
        missingCostRows.push(rowOrder);
      }
    });

    const moduleHours = calculationModuleHoursTotal(authoritativeCalculation);
    if (moduleHours > 0) {
      hoursSum += moduleHours;
      hasHours = true;
    }

    // Prefer API enterprise summary for material/labor/hours when present.
    if (apiSummary) {
      const summaryMaterial = parseFormattedNumber(apiSummary.material_cost);
      const summaryLabor = parseFormattedNumber(apiSummary.labor_cost);
      const summaryHours = parseFormattedNumber(apiSummary.hours_total);
      if (summaryMaterial != null) {
        materialCost = summaryMaterial;
        hasMaterial = true;
      }
      if (summaryLabor != null) {
        laborCost = summaryLabor;
        hasLabor = true;
      }
      if (summaryHours != null && summaryHours > 0) {
        hoursSum = summaryHours;
        hasHours = true;
      }
    }

    if (calculationV2Enabled) {
      const cost = resolveCalculationCostTotal(authoritativeCalculation, {
        v2: true,
      });
      let margin =
        apiSummary?.db != null || apiSummary?.contribution_margin != null
          ? parseFormattedNumber(
              apiSummary.db ?? apiSummary.contribution_margin
            )
          : resolveCalculationContributionMargin(authoritativeCalculation);
      let marginPercent =
        apiSummary?.dg_percent != null ||
        apiSummary?.contribution_margin_percent != null
          ? parseFormattedNumber(
              apiSummary.dg_percent ?? apiSummary.contribution_margin_percent
            )
          : resolveCalculationContributionMarginPercent(
              authoritativeCalculation
            );
      // Keep DB/DG coherent when footer sales falls back to line sum.
      if (salesDisp.estimate && cost != null && sales > 0) {
        margin = sales - cost;
        marginPercent = (margin / sales) * 100;
      }
      return {
        sales,
        salesAuthoritative: salesDisp.authoritative,
        salesEstimate: salesDisp.estimate,
        cost,
        costEstimate: cost == null,
        margin,
        marginPercent,
        materialCost: hasMaterial ? materialCost : null,
        laborCost: hasLabor ? laborCost : null,
        moduleCost: hasModule ? moduleCost : null,
        hoursSum: hasHours ? hoursSum : null,
        missingCostRows,
        fromApiSummary: Boolean(apiSummary) && !salesDisp.estimate,
      };
    }
    let cost = 0;
    (items || []).forEach((it) => {
      const qty = parseFormattedNumber(it?.quantity || 0) || 0;
      const unitCost = getRowUnitCost(it) || 0;
      if (qty > 0 && unitCost > 0) cost += unitCost * qty;
    });
    const margin = sales - cost;
    const marginPercent = sales > 0 ? (margin / sales) * 100 : null;
    return {
      sales,
      salesAuthoritative: false,
      salesEstimate: false,
      cost,
      costEstimate: true,
      margin,
      marginPercent,
      legacyEstimate: true,
      materialCost: hasMaterial ? materialCost : null,
      laborCost: hasLabor ? laborCost : null,
      moduleCost: hasModule ? moduleCost : null,
      hoursSum: hasHours ? hoursSum : null,
      missingCostRows,
    };
  };

  const handleRowProductSelect = (row) => {
    setSelectedItemRow(row);
    setProductSelectorOpen(true);
  };

  const appendProductsAsNotes = (item, products) => {
    const notes = [...(item.notes || [])];
    products.forEach((prod) => {
      const title =
        prod.displayTitle || prod.title || prod.name || `Produkt #${prod.id}`;
      const already = notes.some(
        (n) =>
          n.source_type === "product" &&
          String(n.source_id) === String(prod.id)
      );
      if (already) return;
      notes.push({
        title,
        text: title,
        show_in_offer: false,
        source_type: "product",
        source_id: prod.id,
      });
    });
    return { ...item, notes };
  };

  const handleProductSelect = (data) => {
    setProductSelectorOpen(false);
    if (selectedItemRow && data && data.length > 0) {
      let prevItems = [...items];
      let item = prevItems[selectedItemRow.row_order];

      // Hours lines: products are notes only — never product_id / product_plus_hours
      if (item.line_type === "hours") {
        item = appendProductsAsNotes(item, data);
        item = clearHoursLineProductBinding(item);
      } else if (selectedItemForCalculation) {
        const products = item.products || [];
        const newProducts = [...products];

        data.forEach((prod) => {
          const exists = newProducts.find((p) => p.id === prod.id);
          if (!exists) {
            newProducts.push({
              id: prod.id,
              displayTitle: prod.displayTitle,
              sales_price: prod.sales_price || 0,
              purchase_price: prod.purchase_price || 0,
              quantity: 1,
              discount: 0,
              surcharge: 0,
              divide_by_quantity: false,
              show_image: false,
              show_url: false,
              unit: prod.unit,
            });
          }
        });

        item.products = newProducts;
      } else {
        item.product = data[0];
        item.product_id = data[0].id;
        item.unit_id = data[0].unit?.id;
        item.unit = data[0].unit;
        item.sales_price = data[0].sales_price || 0;
        item.purchase_price = data[0].purchase_price || 0;
        item.quantity =
          item.quantity && parseLooseNumber(item.quantity) > 0
            ? item.quantity
            : "1.0";
        if (item.line_type === "product" && item.show_hours) {
          const mode = readHoursPricingMode(item);
          if (!mode) item = patchHoursPricingMode(item, "product_plus_hours");
        }
      }

      prevItems[selectedItemRow.row_order] = item;
      setItems(prevItems);

      const mappedItems = prevItems.map((row) => ({
        ...row,
        item_type: row.item_type || row.type,
        product_id: row.product_id || row.product?.id || null,
        unit_id: row.unit_id || row.unit?.id || null,
      }));
      setFieldValue(name, mappedItems);

      setSelectedItemForCalculation(null);
    }
  };

  const handleProductFieldChange = (item, productId, field, value) => {
    let prevItems = [...items];
    let updatedItem = {
      ...prevItems[item.row_order],
      products: (item.products || []).map((prod) => {
        if (prod.id === productId) {
          return {
            ...prod,
            [field]: value,
          };
        }
        return prod;
      }),
    };
    prevItems[item.row_order] = updatedItem;
    setItems(prevItems);
    // Update Formik form
    const mappedItems = prevItems.map(item => ({
      ...item,
      item_type: item.item_type || item.type,
      product_id: item.product_id || item.product?.id || null,
      unit_id: item.unit_id || item.unit?.id || null,
    }));
    setFieldValue(name, mappedItems);
  };

  const handleRemoveNestedCalculation = (item, calcId) => {
    let prevItems = [...items];
    let updatedItem = {
      ...prevItems[item.row_order],
      nested_calculation: null,
      nested_calculation_id: null,
    };
    prevItems[item.row_order] = updatedItem;
    setItems(prevItems);
  };

  const patchNestedOnItem = (item, nextNested) => {
    const prevItems = [...items];
    const idx = prevItems.findIndex(
      (row) =>
        row === item ||
        (item?.row_order != null && row.row_order === item.row_order)
    );
    if (idx < 0) return;
    const current = prevItems[idx];
    const qty = parseFormattedNumber(current.quantity || 0);
    const effectiveQty = qty > 0 ? qty : 1;
    const unitSales =
      nextNested?.total_amount != null
        ? parseFloat(nextNested.total_amount)
        : null;
    const unitCost =
      nextNested?.purchase_amount != null
        ? parseFloat(nextNested.purchase_amount)
        : nextNested?.cost_total != null
          ? parseFloat(nextNested.cost_total)
          : null;
    const patched = {
      ...current,
      nested_calculation_id:
        nextNested?.id || current.nested_calculation_id,
      nested_calculation: {
        ...(current.nested_calculation || {}),
        ...nextNested,
      },
      ...(unitSales != null && Number.isFinite(unitSales)
        ? { sales_price: unitSales }
        : {}),
      ...(unitCost != null && Number.isFinite(unitCost)
        ? { purchase_price: unitCost }
        : {}),
    };
    prevItems[idx] = {
      ...patched,
      line_total: lineAmount({
        ...patched,
        quantity: effectiveQty,
      }),
      ...(unitCost != null && Number.isFinite(unitCost)
        ? { cost_total: unitCost * effectiveQty }
        : {}),
    };
    setItems(prevItems);
  };

  const handleRemoveProduct = (item, productId) => {
    let prevItems = [...items];
    let updatedItem = {
      ...prevItems[item.row_order],
      products: (item.products || []).filter((p) => p.id !== productId),
    };
    prevItems[item.row_order] = updatedItem;
    setItems(prevItems);
  };

  const deleteRow = (rowOrder) => {
    const item = items.find((row) => sameRowOrder(row?.row_order, rowOrder));
    deleteTargetRef.current = {
      rowOrder: rowOrder != null && rowOrder !== "" ? Number(rowOrder) : null,
      id: item?.id ?? null,
      draggableId: item?.draggableId ?? null,
    };
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteRow = () => {
    setDeleteConfirmationOpen(false);
    const target = deleteTargetRef.current;
    deleteTargetRef.current = null;
    if (!target) return;
    const removed =
      items.find((item) => {
        if (target.id != null && item?.id != null && String(item.id) === String(target.id)) {
          return true;
        }
        if (target.draggableId && item?.draggableId === target.draggableId) {
          return true;
        }
        return sameRowOrder(item?.row_order, target.rowOrder);
      }) || target;
    markPendingDeleted(removed);
    const remaining = items.filter((item) => {
      if (target.id != null && item?.id != null && String(item.id) === String(target.id)) {
        return false;
      }
      if (target.draggableId && item?.draggableId === target.draggableId) {
        return false;
      }
      return !sameRowOrder(item?.row_order, target.rowOrder);
    });
    commitItems(remaining, { persist: true });
  };

  // offer-17: multi-select helpers. Selection is keyed by row_order (positional
  // after orderItems); we clear it whenever the row set changes structurally.
  const clearSelection = () => {
    lastSelectedIndexRef.current = null;
    setSelectedRowOrders((prev) => (prev.size ? new Set() : prev));
  };

  const toggleRowSelection = (rowOrder, index, shiftKey) => {
    setSelectedRowOrders((prev) => {
      const next = new Set(prev);
      const anchor = lastSelectedIndexRef.current;
      if (shiftKey && anchor != null) {
        const start = Math.min(anchor, index);
        const end = Math.max(anchor, index);
        for (let i = start; i <= end; i += 1) {
          const ro = items[i]?.row_order;
          if (ro != null) next.add(ro);
        }
      } else if (next.has(rowOrder)) {
        next.delete(rowOrder);
      } else {
        next.add(rowOrder);
      }
      return next;
    });
    lastSelectedIndexRef.current = index;
  };

  const allRowsSelected =
    items.length > 0 && selectedRowOrders.size >= items.length;

  const toggleSelectAll = () => {
    if (allRowsSelected) {
      clearSelection();
      return;
    }
    setSelectedRowOrders(
      new Set(
        items
          .map((item) => item.row_order)
          .filter((ro) => ro != null)
      )
    );
    lastSelectedIndexRef.current = null;
  };

  const handleDeleteSelected = () => {
    if (selectedRowOrders.size === 0) return;
    items.forEach((item) => {
      if (selectedRowOrders.has(item.row_order) || selectedRowOrders.has(Number(item.row_order))) {
        markPendingDeleted(item);
      }
    });
    const remaining = items.filter(
      (item) =>
        !selectedRowOrders.has(item.row_order) &&
        !selectedRowOrders.has(Number(item.row_order))
    );
    commitItems(remaining, { persist: true });
    if (workspaceMode && selectedRowOrder != null && onSelectRow) {
      onSelectRow(null);
    }
    clearSelection();
    setBulkDeleteConfirmOpen(false);
  };

  useEffect(() => {
    if (!workspaceMode || selectedRowOrder == null) return;
    const el = document.querySelector(
      `[data-offer-calc-row="${selectedRowOrder}"]`
    );
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [workspaceMode, selectedRowOrder]);

  // Keep right-hand properties in sync with local grid rows (Formik lags until blur).
  useEffect(() => {
    if (typeof onContextItemChange !== "function") return;
    if (!workspaceMode || selectedRowOrder == null) {
      onContextItemChange(null);
      return;
    }
    const idx = (items || []).findIndex((item, i) => {
      const order =
        item.row_order !== undefined && item.row_order !== null
          ? item.row_order
          : i;
      return Number(order) === Number(selectedRowOrder);
    });
    onContextItemChange(idx >= 0 ? items[idx] : null);
  }, [workspaceMode, selectedRowOrder, items, onContextItemChange]);

  const renderItemRow = (item, index) => {
    const isProductType = calculationType === "product" || item.item_type === "product";
    const rowOrder =
      item.row_order !== undefined && item.row_order !== null
        ? item.row_order
        : index;
    const isSelected =
      workspaceMode &&
      selectedRowOrder != null &&
      Number(selectedRowOrder) === Number(rowOrder);

    const lineType = item?.line_type || "manual";

    return (
      <div
        key={item.draggableId || index}
        data-offer-calc-row={rowOrder}
        className={[
          "offer-calc-line-card",
          workspaceMode
            ? "offer-calc-line-card--workspace"
            : "offer-calc-line-card--classic",
          `offer-calc-line-card--${lineType}`,
          isSelected ? "is-selected" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={(e) => {
          if (workspaceMode) {
            e.stopPropagation();
            return;
          }
        }}
      >
        {/* Standard Fields Section */}
        <div className="offer-calc-line-card__body">
          {workspaceMode ? (
            lineType === "calculation" ? (
              <div className="offer-calc-line-card__toolbar">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedItemForSettings(item);
                    setCalculationSettingsDialogOpen(true);
                  }}
                  title={t("Innstillinger for beregningslinje")}
                  className="text-gray-600 hover:text-indigo-600"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                </IconButton>
              </div>
            ) : null
          ) : (
          <div className="offer-calc-line-card__head">
            <div className="offer-calc-line-card__head-left">
              {lineType === "hours" && (
                <span className="offer-calc-line-type-hint">
                  {t("offer.calculation.line_editor.hours_focus_hint")}
                </span>
              )}
              {lineType === "product" && (
                <span className="offer-calc-line-type-hint">
                  {t("offer.calculation.line_editor.product_focus_hint")}
                </span>
              )}
              {lineType === "manual" && (
                <span className="offer-calc-line-type-hint">
                  {t("offer.calculation.line_editor.manual_focus_hint")}
                </span>
              )}
              {lineType === "calculation" && (
                <span className="offer-calc-line-type-hint">
                  {t("offer.calculation.line_editor.calculation_focus_hint")}
                </span>
              )}
              {lineType === "note" && (
                <span className="offer-calc-line-type-hint">
                  {t("offer.calculation.line_editor.note_focus_hint")}
                </span>
              )}
            </div>
            <div className="offer-calc-line-card__head-right">
              {lineType === "calculation" && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedItemForSettings(item);
                  setCalculationSettingsDialogOpen(true);
                }}
                title={t("Innstillinger for beregningslinje")}
                className="text-gray-600 hover:text-indigo-600"
              >
                <PencilSquareIcon className="h-4 w-4" />
              </IconButton>
              )}
              <div className={`offer-calc-line-type-badge is-${workspaceBadgeType(lineType)}`}>
                {lineTypeIconEl(lineType === "sale" ? "product" : lineType, "h-3.5 w-3.5")}
                <span>{t(WORKSPACE_LINE_TYPE_LABEL_KEY(lineType))}</span>
              </div>
            </div>
          </div>
          )}
          <div className="offer-calc-line-card__fields">
          <div className="grid grid-cols-12 gap-4">
            {lineType === "product" && (
              <div className="col-span-12 sm:col-span-3">
                <div className="text-xs font-semibold text-slate-500 mb-1.5">
                  {t("offer.calculation.workspace.line_type.product")}
                </div>
                <button
                  type="button"
                  className="offer-calc-line-product-pick"
                  onClick={() => handleRowProductSelect(item)}
                >
                  <span className="offer-calc-line-product-pick__glyph" aria-hidden>
                    P
                  </span>
                  {item?.product?.id ? (
                    <span className="offer-calc-line-product-pick__title">
                      {item?.product?.displayTitle}
                    </span>
                  ) : (
                    <span className="offer-calc-line-product-pick__title is-empty">
                      {t("offer.calculation.line_editor.select_product")}
                    </span>
                  )}
                  <svg
                    className="offer-calc-line-product-pick__chevron"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M6 9l6 6 6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )}

            {lineType === "note" ? (
              <div className="col-span-12 offer-calc-line-desc--focus offer-calc-line-note-editor">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item?.show_note_title === true}
                      onChange={(e) => {
                        let prevItems = [...items];
                        if (!prevItems[item.row_order]) return;
                        prevItems[item.row_order] = {
                          ...prevItems[item.row_order],
                          show_note_title: e.target.checked,
                        };
                        setItems(prevItems);
                      }}
                      size="small"
                    />
                  }
                  label={t("offer.calculation.line_editor.show_note_title")}
                />
                {item?.show_note_title === true && (
                  <TextField
                    label={t("offer.calculation.line_editor.note_title")}
                    variant="standard"
                    fullWidth
                    className="mb-3"
                    value={item?.note_title || ""}
                    onChange={(e) => {
                      let prevItems = [...items];
                      if (!prevItems[item.row_order]) return;
                      prevItems[item.row_order] = {
                        ...prevItems[item.row_order],
                        note_title: e.target.value,
                      };
                      setItems(prevItems);
                    }}
                    placeholder={t(
                      "offer.calculation.line_editor.note_title_placeholder"
                    )}
                  />
                )}
                <div className="text-xs font-semibold text-slate-500 mb-1.5">
                  {t("offer.calculation.line_editor.note_text")}
                </div>
                <PromangRichTextEditor
                  value={item?.description || ""}
                  onChange={(html) => {
                    let prevItems = [...items];
                    if (!prevItems[item.row_order]) return;
                    prevItems[item.row_order] = {
                      ...prevItems[item.row_order],
                      description: html,
                    };
                    setItems(prevItems);
                  }}
                  placeholder={t("offer.calculation.line_editor.note_placeholder")}
                  size="md"
                  templatesEnabled
                  templateButtonLabel={t(
                    "offer.calculation.line_editor.note_template_button"
                  )}
                  tablesEnabled
                />
              </div>
            ) : (
            <div
              className={
                lineType === "hours"
                  ? "col-span-12 sm:col-span-8 offer-calc-line-desc--focus"
                  : lineType === "product"
                  ? "col-span-12 sm:col-span-4"
                  : "col-span-12 sm:col-span-6"
              }
            >
              <TextField
                label={t("offer.calculation.workspace.col.description")}
                variant="standard"
                fullWidth
                name="description"
                value={item?.description || ""}
                onChange={(e) => onItemRowChange(e, item)}
                multiline
                rows={lineType === "hours" ? 3 : 2}
                autoFocus={lineType === "hours" && isSelected}
              />
            </div>
            )}
            {lineType !== "note" && (
            <>
            {/* Quantity - Available for all types */}
            <div className="col-span-6 sm:col-span-2">
              <div className="flex items-center gap-2">
                <TextField
                  label={t("offer.calculation.line_editor.quantity")}
                  variant="standard"
                  type="text"
                  name="quantity"
                  value={item?.quantity || 0}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    const cleanedValue = rawValue.replace(",", ".");
                    // Update directly instead of using onItemRowChange to avoid preventDefault issues
                    let prevItems = [...items];
                    prevItems[item.row_order] = {
                      ...prevItems[item.row_order],
                      quantity: cleanedValue,
                    };
                    commitItemRow(prevItems, item.row_order);
                  }}
                  onBlur={(e) => {
                    const formatted = formatNumber(e.target.value);
                    let prevItems = [...items];
                    prevItems[item.row_order] = {
                      ...prevItems[item.row_order],
                      quantity: formatted,
                    };
                    
                    // Recalculate hours if auto-calculate is enabled when quantity changes
                    if (prevItems[item.row_order]?.calculate_hours_from_sales && prevItems[item.row_order]?.line_type === "hours") {
                      const salesPrice = parseFormattedNumber(prevItems[item.row_order].sales_price || 0);
                      const quantity = parseFormattedNumber(formatted || 1);
                      const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                      const hourRate = parseFormattedNumber(prevItems[item.row_order].hour_rate || 0);
                      if (hourRate > 0 && totalSalesPrice > 0) {
                        const calculatedHours = totalSalesPrice / hourRate;
                        prevItems[item.row_order].hours = calculatedHours.toFixed(2);
                        prevItems[item.row_order].calculated_hours_from_sales = calculatedHours;
                      }
                    }
                    
                    // Recalculate worst case hours when quantity changes
                    if (prevItems[item.row_order]?.line_type === "hours" && prevItems[item.row_order]?.cost_hour_rate) {
                      const salesPrice = parseFormattedNumber(prevItems[item.row_order].sales_price || 0);
                      const quantity = parseFormattedNumber(formatted || 1);
                      const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                      const costHourRate = parseFormattedNumber(prevItems[item.row_order].cost_hour_rate || 0);
                      if (costHourRate > 0 && totalSalesPrice > 0) {
                        const calculatedHours = totalSalesPrice / costHourRate;
                        prevItems[item.row_order].calculated_hours_from_cost = calculatedHours;
                      }
                    }
                    
                    commitItemRow(prevItems, item.row_order);
                  }}
                  fullWidth
                  required={item?.required_fields?.quantity}
                  disabled={!item?.editable_quantity}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    let prevItems = [...items];
                    const currentRequired = prevItems[item.row_order].required_fields || {};
                    prevItems[item.row_order] = {
                      ...prevItems[item.row_order],
                      required_fields: {
                        ...currentRequired,
                        quantity: !currentRequired.quantity,
                      },
                    };
                    setItems(prevItems);
                  }}
                  title={item?.required_fields?.quantity ? "Ikke påkrevd" : "Gjør påkrevd"}
                >
                  {item?.required_fields?.quantity ? (
                    <span className="text-red-500 text-xs">*</span>
                  ) : (
                    <span className="text-gray-400 text-xs">*</span>
                  )}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => {
                    let prevItems = [...items];
                    prevItems[item.row_order] = {
                      ...prevItems[item.row_order],
                      editable_quantity: !prevItems[item.row_order].editable_quantity,
                    };
                    setItems(prevItems);
                  }}
                  title={item?.editable_quantity ? "Ikke redigerbar i tilbud" : "Redigerbar i tilbud"}
                >
                  <PencilSquareIcon className={`h-4 w-4 ${item?.editable_quantity ? "text-blue-500" : "text-gray-400"}`} />
                </IconButton>
              </div>
            </div>

            {/* Unit - Available for all types */}
            <div className="col-span-6 sm:col-span-2">
              <TextField
                label={t("offer.calculation.workspace.col.unit")}
                variant="standard"
                disabled
                value={
                  lineType === "hours"
                    ? t("offer.calculation.line_editor.unit_hours")
                    : item?.unit?.title || "Stk"
                }
                fullWidth
              />
            </div>
            </>
            )}

          </div>

          {lineType !== "note" && (
          <div className="grid grid-cols-12 gap-4 offer-calc-line-card__price-row">
            {lineType === "hours" && (
              <div className="col-span-12 sm:col-span-3">
                <FormControl variant="standard" fullWidth disabled>
                  <InputLabel>
                    {t("offer.calculation.line_editor.price_mode")}
                  </InputLabel>
                  <Select
                    name="price_mode"
                    value="automatic"
                    label={t("offer.calculation.line_editor.price_mode")}
                  >
                    <MenuItem value="automatic">
                      {t("offer.calculation.line_editor.price_mode_automatic")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
            )}
            {lineType !== "manual" &&
              lineType !== "product" &&
              lineType !== "hours" &&
              lineType !== "note" && (
              <div className="col-span-12 sm:col-span-3">
                <FormControl variant="standard" fullWidth>
                  <InputLabel>
                    {t("offer.calculation.line_editor.price_mode")}
                  </InputLabel>
                  <Select
                    name="price_mode"
                    value={item?.price_mode || "custom"}
                    onChange={(e) => onItemRowChange(e, item)}
                    label={t("offer.calculation.line_editor.price_mode")}
                  >
                    <MenuItem value="automatic">
                      {t("offer.calculation.line_editor.price_mode_automatic")}
                    </MenuItem>
                    <MenuItem value="custom">
                      {t("offer.calculation.line_editor.price_mode_custom")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
            )}

            {/* Prices - Show based on mode */}
            {lineType === "manual" || lineType === "product" ? (
              // Manual lines - always editable, no price mode selection
              <>
                <div className="col-span-6 sm:col-span-2">
                  <TextField
                    label={t("offer.calculation.workspace.col.sales_price")}
                    variant="standard"
                    type="text"
                    name="sales_price"
                    value={item?.sales_price || 0}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      const cleanedValue = rawValue.replace(",", ".");
                      let prevItems = [...items];
                      prevItems[item.row_order] = {
                        ...prevItems[item.row_order],
                        sales_price: cleanedValue,
                      };
                      commitItemRow(prevItems, item.row_order);
                    }}
                    onBlur={(e) => {
                      const formatted = formatNumber(e.target.value);
                      let prevItems = [...items];
                      prevItems[item.row_order] = {
                        ...prevItems[item.row_order],
                        sales_price: formatted,
                      };
                      commitItemRow(prevItems, item.row_order);
                    }}
                    fullWidth
                  />
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <TextField
                    label={t("offer.calculation.workspace.col.cost_price")}
                    variant="standard"
                    type="text"
                    name="purchase_price"
                    value={item?.purchase_price || 0}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      const cleanedValue = rawValue.replace(",", ".");
                      let prevItems = [...items];
                      prevItems[item.row_order] = {
                        ...prevItems[item.row_order],
                        purchase_price: cleanedValue,
                      };
                      commitItemRow(prevItems, item.row_order);
                    }}
                    onBlur={(e) => {
                      const formatted = formatNumber(e.target.value);
                      let prevItems = [...items];
                      prevItems[item.row_order] = {
                        ...prevItems[item.row_order],
                        purchase_price: formatted,
                      };
                      commitItemRow(prevItems, item.row_order);
                    }}
                    fullWidth
                  />
                </div>
              </>
            ) : item?.price_mode === "automatic" ? (
              <>
                <div className="col-span-6 sm:col-span-2">
                  <TextField
                    label={t("offer.calculation.line_editor.sales_price_auto")}
                    variant="standard"
                    type="number"
                    disabled
                    value={calculateAutomaticSalesPrice(item).toFixed(2)}
                    fullWidth
                  />
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <TextField
                    label={t("offer.calculation.line_editor.cost_price_auto")}
                    variant="standard"
                    type="number"
                    disabled
                    value={calculateAutomaticPurchasePrice(item).toFixed(2)}
                    fullWidth
                  />
                </div>
              </>
            ) : (
              <>
                <div className="col-span-6 sm:col-span-2">
                  <TextField
                    label={t("offer.calculation.workspace.col.sales_price")}
                    variant="standard"
                    type="text"
                    name="sales_price"
                    value={item?.sales_price || 0}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      // Allow typing with comma or dot
                      const cleanedValue = rawValue.replace(",", ".");
                      let prevItems = [...items];
                      
                      // Auto-calculate hours if enabled (for hours lines with custom price_mode)
                      if (item?.calculate_hours_from_sales && item?.line_type === "hours") {
                        const salesPrice = parseFormattedNumber(cleanedValue);
                        const quantity = parseFormattedNumber(item?.quantity || 1);
                        const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                        const hourRate = parseFormattedNumber(item?.hour_rate || 0);
                        if (hourRate > 0 && totalSalesPrice > 0) {
                          const calculatedHours = totalSalesPrice / hourRate;
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            sales_price: cleanedValue,
                            hours: calculatedHours.toFixed(2),
                            calculated_hours_from_sales: calculatedHours,
                          };
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            sales_price: cleanedValue,
                          };
                        }
                      } else {
                        // Always calculate worst case hours for hours lines when sales_price changes: (salgspris * mengde) / kostpris timesats
                        if (item?.line_type === "hours" && item?.cost_hour_rate) {
                          const salesPrice = parseFormattedNumber(cleanedValue);
                          const quantity = parseFormattedNumber(item?.quantity || 1);
                          const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                          const costHourRate = parseFormattedNumber(item?.cost_hour_rate || 0);
                          if (costHourRate > 0 && totalSalesPrice > 0) {
                            const calculatedHours = totalSalesPrice / costHourRate;
                            prevItems[item.row_order] = {
                              ...prevItems[item.row_order],
                              sales_price: cleanedValue,
                              calculated_hours_from_cost: calculatedHours,
                            };
                          } else {
                            prevItems[item.row_order] = {
                              ...prevItems[item.row_order],
                              sales_price: cleanedValue,
                            };
                          }
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            sales_price: cleanedValue,
                          };
                        }
                      }
                      commitItemRow(prevItems, item.row_order);
                    }}
                    onBlur={(e) => {
                      const formatted = formatNumber(e.target.value);
                      let prevItems = [...items];
                      
                      // Recalculate hours if auto-calculate is enabled (for hours lines)
                      if (item?.calculate_hours_from_sales && item?.line_type === "hours") {
                        const salesPrice = parseFormattedNumber(formatted);
                        const quantity = parseFormattedNumber(item?.quantity || 1);
                        const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                        const hourRate = parseFormattedNumber(item?.hour_rate || 0);
                        if (hourRate > 0 && totalSalesPrice > 0) {
                          const calculatedHours = totalSalesPrice / hourRate;
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            sales_price: formatted,
                            hours: calculatedHours.toFixed(2),
                            calculated_hours_from_sales: calculatedHours,
                          };
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            sales_price: formatted,
                          };
                        }
                      } else {
                        // Always calculate worst case hours for hours lines when sales_price changes: (salgspris * mengde) / kostpris timesats
                        if (item?.line_type === "hours" && item?.cost_hour_rate) {
                          const salesPrice = parseFormattedNumber(formatted);
                          const quantity = parseFormattedNumber(item?.quantity || 1);
                          const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                          const costHourRate = parseFormattedNumber(item?.cost_hour_rate || 0);
                          if (costHourRate > 0 && totalSalesPrice > 0) {
                            const calculatedHours = totalSalesPrice / costHourRate;
                            prevItems[item.row_order] = {
                              ...prevItems[item.row_order],
                              sales_price: formatted,
                              calculated_hours_from_cost: calculatedHours,
                            };
                          } else {
                            prevItems[item.row_order] = {
                              ...prevItems[item.row_order],
                              sales_price: formatted,
                            };
                          }
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            sales_price: formatted,
                          };
                        }
                      }
                      commitItemRow(prevItems, item.row_order);
                    }}
                    disabled={item?.calculate_hours_from_cost && item?.line_type === "hours"}
                    fullWidth
                  />
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <TextField
                    label={t("offer.calculation.workspace.col.cost_price")}
                    variant="standard"
                    type="text"
                    name="purchase_price"
                    value={item?.purchase_price || 0}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      // Allow typing with comma or dot
                      const cleanedValue = rawValue.replace(",", ".");
                      let prevItems = [...items];
                      
                      // Auto-calculate hours if enabled (for hours lines with custom price_mode)
                      if (item?.calculate_hours_from_cost && item?.line_type === "hours") {
                        const costPrice = parseFormattedNumber(cleanedValue);
                        const hourRate = parseFormattedNumber(item?.hour_rate || 0);
                        if (hourRate > 0 && costPrice > 0) {
                          const calculatedHours = costPrice / hourRate;
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            purchase_price: cleanedValue,
                            calculated_hours_from_cost: calculatedHours,
                          };
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            purchase_price: cleanedValue,
                          };
                        }
                      } else {
                        // Always calculate worst case hours for hours lines (even if checkbox not checked)
                        if (item?.line_type === "hours" && item?.hour_rate) {
                          const costPrice = parseFormattedNumber(cleanedValue);
                          const hourRate = parseFormattedNumber(item?.hour_rate || 0);
                          if (hourRate > 0 && costPrice > 0) {
                            const calculatedHours = costPrice / hourRate;
                            prevItems[item.row_order] = {
                              ...prevItems[item.row_order],
                              purchase_price: cleanedValue,
                              calculated_hours_from_cost: calculatedHours,
                            };
                          } else {
                            prevItems[item.row_order] = {
                              ...prevItems[item.row_order],
                              purchase_price: cleanedValue,
                            };
                          }
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            purchase_price: cleanedValue,
                          };
                        }
                      }
                      commitItemRow(prevItems, item.row_order);
                    }}
                    onBlur={(e) => {
                      const formatted = formatNumber(e.target.value);
                      let prevItems = [...items];
                      
                      // Recalculate hours if auto-calculate is enabled (for hours lines)
                      if (item?.calculate_hours_from_cost && item?.line_type === "hours") {
                        const costPrice = parseFormattedNumber(formatted);
                        const hourRate = parseFormattedNumber(item?.hour_rate || 0);
                        if (hourRate > 0 && costPrice > 0) {
                          const calculatedHours = costPrice / hourRate;
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            purchase_price: formatted,
                            calculated_hours_from_cost: calculatedHours,
                          };
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            purchase_price: formatted,
                          };
                        }
                      } else {
                        // Always calculate worst case hours for hours lines (even if checkbox not checked)
                        if (item?.line_type === "hours" && item?.hour_rate) {
                          const costPrice = parseFormattedNumber(formatted);
                          const hourRate = parseFormattedNumber(item?.hour_rate || 0);
                          if (hourRate > 0 && costPrice > 0) {
                            const calculatedHours = costPrice / hourRate;
                            prevItems[item.row_order] = {
                              ...prevItems[item.row_order],
                              purchase_price: formatted,
                              calculated_hours_from_cost: calculatedHours,
                            };
                          } else {
                            prevItems[item.row_order] = {
                              ...prevItems[item.row_order],
                              purchase_price: formatted,
                            };
                          }
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            purchase_price: formatted,
                          };
                        }
                      }
                      commitItemRow(prevItems, item.row_order);
                    }}
                    disabled={item?.calculate_hours_from_sales && item?.line_type === "hours"}
                    fullWidth
                  />
                </div>
              </>
            )}

            {/* Discount - Available for all types */}
            <div className="col-span-6 sm:col-span-2">
              <TextField
                label={t("offer.calculation.line_editor.discount_pct")}
                variant="standard"
                type="text"
                name="discount"
                value={item?.discount || 0}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  const cleanedValue = rawValue.replace(",", ".");
                  let prevItems = [...items];
                  prevItems[item.row_order] = {
                    ...prevItems[item.row_order],
                    discount: cleanedValue,
                  };
                  commitItemRow(prevItems, item.row_order);
                }}
                onBlur={(e) => {
                  const formatted = formatNumber(e.target.value);
                  let prevItems = [...items];
                  prevItems[item.row_order] = {
                    ...prevItems[item.row_order],
                    discount: formatted,
                  };
                  commitItemRow(prevItems, item.row_order);
                }}
                fullWidth
              />
            </div>

            {/* Surcharge - Available for all types; CALC-001 pricing method as field suffix */}
            <div className="col-span-6 sm:col-span-2 offer-calc-line-card__markup-field">
              <TextField
                label={t("offer.calculation.line_editor.surcharge_pct")}
                variant="standard"
                type="text"
                name="surcharge"
                value={item?.surcharge || 0}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  const cleanedValue = rawValue.replace(",", ".");
                  let prevItems = [...items];
                  prevItems[item.row_order] = {
                    ...prevItems[item.row_order],
                    surcharge: cleanedValue,
                  };
                  commitItemRow(prevItems, item.row_order);
                }}
                onBlur={(e) => {
                  const formatted = formatNumber(e.target.value);
                  let prevItems = [...items];
                  prevItems[item.row_order] = {
                    ...prevItems[item.row_order],
                    surcharge: formatted,
                  };
                  commitItemRow(prevItems, item.row_order);
                }}
                fullWidth
                InputProps={{
                  readOnly: Boolean(item?.custom_fields?.pricing_method),
                  endAdornment: (
                    <InputAdornment position="end" className="offer-calc-line-card__pricing-method-adornment">
                      <button
                        type="button"
                        className={
                          item?.custom_fields?.pricing_method
                            ? "offer-calc-line-card__pricing-method-btn is-active"
                            : "offer-calc-line-card__pricing-method-btn"
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setPricingDialogItem(item);
                        }}
                        aria-label={t("offers.pricing.open_button")}
                        title={
                          item?.custom_fields?.pricing_method
                            ? t("offers.pricing.surcharge_disabled_hint")
                            : t("offers.pricing.dialog_title")
                        }
                      >
                        {item?.custom_fields?.pricing_method === "markup"
                          ? t("offers.pricing.method_markup")
                          : item?.custom_fields?.pricing_method === "target_margin"
                            ? t("offers.pricing.method_target_margin")
                            : item?.custom_fields?.pricing_method === "fixed_sales"
                              ? t("offers.pricing.method_fixed_sales")
                              : t("offers.pricing.open_button")}
                      </button>
                    </InputAdornment>
                  ),
                }}
                className={
                  item?.custom_fields?.pricing_method
                    ? "offer-calc-line-card__surcharge-input is-method-locked"
                    : "offer-calc-line-card__surcharge-input"
                }
              />
            </div>
          </div>
          )}
          </div>
        </div>

        {/* Hours Calculation Section - Show when enabled; not for manual lines */}
        {item?.show_hours && item?.line_type !== "manual" && item?.line_type !== "note" && (
          <div
            className={
              item?.line_type === "hours"
                ? "offer-calc-hours-block offer-calc-hours-block--hours"
                : "offer-calc-hours-block"
            }
          >
            <div className="offer-calc-hours-block__head">
              <div className="offer-calc-hours-block__title">
                {item?.line_type === "hours" && (
                  <ClockIcon className="h-4 w-4" aria-hidden />
                )}
                {t("offer.calculation.line_editor.hours_section")}
              </div>
              {item?.line_type !== "hours" && (
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  let prevItems = [...items];
                  prevItems[item.row_order] = withLocalLineMoneyCaches(
                    patchHoursPricingMode(
                      {
                        ...prevItems[item.row_order],
                        show_hours: false,
                      },
                      null
                    )
                  );
                  setItems(prevItems);
                }}
              >
                {t("offer.calculation.line_editor.hide_hours")}
              </Button>
              )}
            </div>

            {/* Product lines: choose additive time vs derive-from-price */}
            {supportsProductPlusHoursUi(item) && (
              <div className="offer-calc-hours-mode">
                <div className="text-xs font-semibold text-slate-500 mb-1.5">
                  {t("offer.calculation.line_editor.hours_pricing_mode")}
                </div>
                <FormControl fullWidth size="small" variant="outlined">
                  <Select
                    value={
                      isProductPlusHoursMode(item)
                        ? "product_plus_hours"
                        : "from_price"
                    }
                    onChange={(e) => {
                      const mode =
                        e.target.value === "product_plus_hours"
                          ? "product_plus_hours"
                          : "from_price";
                      let prevItems = [...items];
                      const next = patchHoursPricingMode(
                        prevItems[item.row_order],
                        mode
                      );
                      prevItems[item.row_order] = withLocalLineMoneyCaches(next);
                      setItems(prevItems);
                    }}
                    sx={{
                      borderRadius: "10px",
                      fontWeight: 600,
                      backgroundColor: "#fff",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#bdd4f5",
                        borderWidth: "1.5px",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1463c7",
                      },
                    }}
                  >
                    <MenuItem value="product_plus_hours">
                      {t(
                        "offer.calculation.line_editor.hours_mode_product_plus_hours"
                      )}
                    </MenuItem>
                    <MenuItem value="from_price">
                      {t(
                        "offer.calculation.line_editor.hours_mode_from_price"
                      )}
                    </MenuItem>
                  </Select>
                </FormControl>
                {isProductPlusHoursMode(item) && (
                  <div className="offer-calc-hours-mode__hint">
                    {t(
                      "offer.calculation.line_editor.hours_mode_product_plus_hours_hint"
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Auto-calculate hours from price — hours lines, or product when not additive */}
            {((item?.line_type === "hours" && item?.price_mode === "custom" && !isProductPlusHoursMode(item)) ||
              (supportsProductPlusHoursUi(item) && !isProductPlusHoursMode(item))) && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  {t("offer.calculation.line_editor.auto_calc_hours")}
                </div>
                <div className="flex items-center gap-4">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={item?.calculate_hours_from_sales || false}
                        onChange={(e) => {
                          let prevItems = [...items];
                          const checked = e.target.checked;
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            calculate_hours_from_sales: checked,
                            calculate_hours_from_cost: checked ? false : prevItems[item.row_order].calculate_hours_from_cost,
                          };
                          // Auto-calculate if enabled
                          if (checked) {
                            const salesPrice = parseFormattedNumber(prevItems[item.row_order].sales_price || 0);
                            const quantity = parseFormattedNumber(prevItems[item.row_order].quantity || 1);
                            const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                            const hourRate = parseFormattedNumber(prevItems[item.row_order].hour_rate || 0);
                            if (hourRate > 0 && totalSalesPrice > 0) {
                              const calculatedHours = totalSalesPrice / hourRate;
                              prevItems[item.row_order].hours = calculatedHours.toFixed(2);
                              prevItems[item.row_order].calculated_hours_from_sales = calculatedHours;
                            }
                          } else {
                            // Clear calculated hours when unchecked
                            prevItems[item.row_order].calculated_hours_from_sales = null;
                          }
                          setItems(prevItems);
                        }}
                      />
                    }
                    label={t("offer.calculation.line_editor.from_sales_price")}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={item?.calculate_hours_from_cost || false}
                        onChange={(e) => {
                          let prevItems = [...items];
                          const checked = e.target.checked;
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            calculate_hours_from_cost: checked,
                            calculate_hours_from_sales: checked ? false : prevItems[item.row_order].calculate_hours_from_sales,
                          };
                          // Auto-calculate if enabled
                          if (checked) {
                            const costPrice = parseFormattedNumber(prevItems[item.row_order].purchase_price || 0);
                            const hourRate = parseFormattedNumber(prevItems[item.row_order].hour_rate || 0);
                            if (hourRate > 0 && costPrice > 0) {
                              const calculatedHours = costPrice / hourRate;
                              prevItems[item.row_order].calculated_hours_from_cost = calculatedHours;
                            }
                          } else {
                            // Clear calculated hours when unchecked
                            prevItems[item.row_order].calculated_hours_from_cost = null;
                          }
                          setItems(prevItems);
                        }}
                      />
                    }
                    label={t("offer.calculation.line_editor.from_cost_price")}
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-6 sm:col-span-3">
                <TextField
                  label={t("offer.calculation.line_editor.hours_count")}
                  variant="standard"
                  type="text"
                  name="hours"
                  value={item?.hours || 0}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    const cleanedValue = rawValue.replace(",", ".");
                    let prevItems = [...items];
                    prevItems[item.row_order] = {
                      ...prevItems[item.row_order],
                      hours: cleanedValue,
                    };
                    commitItemRow(prevItems, item.row_order);
                  }}
                  onBlur={(e) => {
                    const formatted = formatNumber(e.target.value);
                    let prevItems = [...items];
                    prevItems[item.row_order] = {
                      ...prevItems[item.row_order],
                      hours: formatted,
                    };
                    commitItemRow(prevItems, item.row_order);
                  }}
                  fullWidth
                  required={item?.required_fields?.hours}
                  disabled={item?.calculate_hours_from_sales || item?.calculate_hours_from_cost}
                />
              </div>
              <div className="col-span-6 sm:col-span-3">
                <div className="flex items-end gap-2">
                  <TextField
                    label={t("offer.calculation.line_editor.hour_rate")}
                    variant="standard"
                    type="text"
                    name="hour_rate"
                    value={item?.hour_rate || 0}
                    InputProps={
                      item?.line_type === "hours" ? { readOnly: true } : undefined
                    }
                    onClick={
                      item?.line_type === "hours"
                        ? () => openHourRateSelector(item)
                        : undefined
                    }
                    helperText={
                      item?.line_type === "hours"
                        ? t("offer.calculation.line_editor.hour_rate_from_catalog")
                        : undefined
                    }
                    onChange={
                      item?.line_type === "hours"
                        ? undefined
                        : (e) => {
                    const rawValue = e.target.value;
                    const cleanedValue = rawValue.replace(",", ".");
                    let prevItems = [...items];
                    
                    // Recalculate hours if auto-calculate is enabled
                    if (item?.calculate_hours_from_sales) {
                      const salesPrice = parseFormattedNumber(item?.sales_price || 0);
                      const quantity = parseFormattedNumber(item?.quantity || 1);
                      const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                      const hourRate = parseFormattedNumber(cleanedValue);
                      if (hourRate > 0 && totalSalesPrice > 0) {
                        const calculatedHours = totalSalesPrice / hourRate;
                        prevItems[item.row_order] = {
                          ...prevItems[item.row_order],
                          hour_rate: cleanedValue,
                          hours: calculatedHours.toFixed(2),
                          calculated_hours_from_sales: calculatedHours,
                        };
                      } else {
                        prevItems[item.row_order] = {
                          ...prevItems[item.row_order],
                          hour_rate: cleanedValue,
                        };
                      }
                    } else if (item?.calculate_hours_from_cost) {
                      const costPrice = parseFormattedNumber(item?.purchase_price || 0);
                      const hourRate = parseFormattedNumber(cleanedValue);
                      if (hourRate > 0 && costPrice > 0) {
                        const calculatedHours = costPrice / hourRate;
                        prevItems[item.row_order] = {
                          ...prevItems[item.row_order],
                          hour_rate: cleanedValue,
                          calculated_hours_from_cost: calculatedHours,
                        };
                      } else {
                        prevItems[item.row_order] = {
                          ...prevItems[item.row_order],
                          hour_rate: cleanedValue,
                        };
                      }
                    } else {
                      // Always calculate worst case hours for hours lines: (salgspris * mengde) / kostpris timesats
                      if (item?.line_type === "hours" && item?.cost_hour_rate) {
                        const salesPrice = parseFormattedNumber(item?.sales_price || 0);
                        const quantity = parseFormattedNumber(item?.quantity || 1);
                        const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                        const costHourRate = parseFormattedNumber(item?.cost_hour_rate || 0);
                        if (costHourRate > 0 && totalSalesPrice > 0) {
                          const calculatedHours = totalSalesPrice / costHourRate;
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            hour_rate: cleanedValue,
                            calculated_hours_from_cost: calculatedHours,
                          };
                        } else {
                          prevItems[item.row_order] = {
                            ...prevItems[item.row_order],
                            hour_rate: cleanedValue,
                          };
                        }
                      } else {
                        prevItems[item.row_order] = {
                          ...prevItems[item.row_order],
                          hour_rate: cleanedValue,
                        };
                      }
                    }
                    commitItemRow(prevItems, item.row_order);
                  }
                    }
                  onBlur={
                    item?.line_type === "hours"
                      ? undefined
                      : (e) => {
                    const formatted = formatNumber(e.target.value);
                    let prevItems = [...items];
                    prevItems[item.row_order] = {
                      ...prevItems[item.row_order],
                      hour_rate: formatted,
                    };
                    commitItemRow(prevItems, item.row_order);
                  }
                  }
                  fullWidth
                  required={item?.required_fields?.hour_rate}
                />
                  {item?.line_type === "hours" && (
                    <IconButton
                      size="small"
                      onClick={() => openHourRateSelector(item)}
                      title={t("offer.calculation.line_editor.select_hour_rate")}
                      className="mb-1"
                    >
                      <PlusIcon className="h-5 w-5" />
                    </IconButton>
                  )}
                </div>
              </div>
              {/* Timekost field - hours lines, or product additive mode */}
              {(item?.line_type === "hours" || isProductPlusHoursMode(item)) && (
                <div className="col-span-6 sm:col-span-3">
                  <TextField
                    label={t("offer.calculation.line_editor.cost_hour_rate")}
                    variant="standard"
                    type="text"
                    name="cost_hour_rate"
                    value={item?.cost_hour_rate || 0}
                    InputProps={
                      item?.line_type === "hours" ? { readOnly: true } : undefined
                    }
                    onClick={
                      item?.line_type === "hours"
                        ? () => openHourRateSelector(item)
                        : undefined
                    }
                    helperText={
                      item?.line_type === "hours"
                        ? t("offer.calculation.line_editor.hour_rate_from_catalog")
                        : undefined
                    }
                    onChange={
                      item?.line_type === "hours"
                        ? undefined
                        : (e) => {
                            const rawValue = e.target.value;
                            const cleanedValue = rawValue.replace(",", ".");
                            let prevItems = [...items];
                            prevItems[item.row_order] = {
                              ...prevItems[item.row_order],
                              cost_hour_rate: cleanedValue,
                            };
                            commitItemRow(prevItems, item.row_order);
                          }
                    }
                    onBlur={
                      item?.line_type === "hours"
                        ? undefined
                        : (e) => {
                            const formatted = formatNumber(e.target.value);
                            let prevItems = [...items];
                            prevItems[item.row_order] = {
                              ...prevItems[item.row_order],
                              cost_hour_rate: formatted,
                            };
                            commitItemRow(prevItems, item.row_order);
                          }
                    }
                    fullWidth
                  />
                </div>
              )}
              <div className="col-span-6 sm:col-span-3">
                <div className="offer-calc-hours-unit-total">
                  <div className="offer-calc-hours-unit-total__label">
                    {isProductPlusHoursMode(item)
                      ? t("offer.calculation.line_editor.unit_with_hours")
                      : t("offer.calculation.line_editor.hours_amount")}
                  </div>
                  <div className="offer-calc-hours-unit-total__amount">
                    {isProductPlusHoursMode(item)
                      ? priceFormatter.format(getRowUnitSales(item))
                      : priceFormatter.format(
                          parseFloat(item?.hours || 0) *
                            parseFloat(item?.hour_rate || 0) *
                            parseFloat(item?.quantity || 1)
                        )}
                  </div>
                </div>
              </div>
            </div>

            {/* Show calculated hours from both sales and cost if available */}
            {(item?.calculated_hours_from_sales || item?.calculated_hours_from_cost || (item?.line_type === "hours" && item?.sales_price && item?.cost_hour_rate)) && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-xs text-gray-600 mb-2">Beregnet timeantall:</div>
                <div className="grid grid-cols-2 gap-4">
                  {item?.calculated_hours_from_sales && (
                    <div>
                      <div className="text-xs text-gray-500">Fra salgspris:</div>
                      <div className="font-semibold text-green-700">
                        {item.calculated_hours_from_sales.toFixed(2)}t
                      </div>
                    </div>
                  )}
                  {(item?.calculated_hours_from_cost || (item?.line_type === "hours" && item?.sales_price && item?.cost_hour_rate)) && (
                    <div>
                      <div className="text-xs text-gray-500">Fra kostpris (worst case):</div>
                      <div className="font-semibold text-orange-700">
                        {(item?.calculated_hours_from_cost || (parseFormattedNumber(item?.sales_price || 0) / parseFormattedNumber(item?.cost_hour_rate || 1))).toFixed(2)}t
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Add Hours Calculation Button - product lines only */}
        {!item?.show_hours && item?.line_type === "product" && (
          <div className="px-5 pb-2 pt-3">
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                let prevItems = [...items];
                const next = {
                  ...prevItems[item.row_order],
                  show_hours: true,
                };
                prevItems[item.row_order] = withLocalLineMoneyCaches(
                  patchHoursPricingMode(next, "product_plus_hours")
                );
                setItems(prevItems);
              }}
              startIcon={<PlusIcon className="h-4 w-4" />}
            >
              {t("offer.calculation.line_editor.add_hours")}
            </Button>
          </div>
        )}

        {item?.line_type === "hours" && (
          <div className="offer-calc-hours-notes">
            <div className="text-sm font-medium text-amber-900 mb-1">
              {t("offer.calculation.line_editor.hours_notes_section")}
            </div>
            <p className="text-xs text-amber-800/80 mb-3">
              {t("offer.calculation.line_editor.hours_notes_hint")}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSelectedItemForCalculationSelect(item);
                  setCalculationSelectorOpen(true);
                }}
                startIcon={<PlusIcon className="h-4 w-4" />}
              >
                {t("offer.calculation.line_editor.add_calc_as_note")}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSelectedItemForCalculation(item);
                  handleRowProductSelect(item);
                }}
                startIcon={<PlusIcon className="h-4 w-4" />}
              >
                {t("offer.calculation.line_editor.add_product_as_note")}
              </Button>
            </div>
          </div>
        )}
        {(item?.line_type === "calculation") && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700 mb-3">
              {t("offer.calculation.line_editor.calcs_and_products")}
            </div>
            <div className="space-y-2">
            {(() => {
              // Get current item from items state
              const currentItem = items.find(i => i.row_order === item.row_order);
              return currentItem?.nested_calculation && (
                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium">{currentItem.nested_calculation.title || `Kalkyle #${currentItem.nested_calculation.id}`}</span>
                    <div className="flex items-center gap-2">
                      {onEditNestedCalculation && (
                        <PencilSquareIcon
                          className="cursor-pointer h-4 w-4 text-blue-500 hover:text-blue-700"
                          onClick={() => onEditNestedCalculation(currentItem.nested_calculation)}
                        />
                      )}
                      <TrashIcon
                        className="cursor-pointer h-4 w-4 text-red-400 hover:text-red-600"
                        onClick={() => handleRemoveNestedCalculation(item, currentItem.nested_calculation.id)}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-24">
                        <TextField
                          label={t("offer.calculation.workspace.col.quantity")}
                          variant="standard"
                          type="number"
                          size="small"
                          fullWidth
                          value={currentItem.quantity || 1}
                          onChange={(e) => {
                            let prevItems = [...items];
                            const itemToUpdate = prevItems.find(i => i.row_order === item.row_order);
                            if (!itemToUpdate) return;
                            prevItems[item.row_order] = {
                              ...itemToUpdate,
                              quantity: parseFloat(e.target.value) || 1,
                            };
                            commitItemRow(prevItems, item.row_order);
                            const mappedItems = prevItems.map(item => ({
                              ...item,
                              item_type: item.item_type || item.type,
                            }));
                            setFieldValue(name, mappedItems);
                          }}
                        />
                      </div>
                      <div className="text-right min-w-[120px]">
                        <div className="text-xs text-gray-500">Total sum:</div>
                        <div className="text-sm font-semibold">
                          {priceFormatter.format(
                            (() => {
                              let nestedAmount = parseFloat(currentItem.nested_calculation.total_amount || 0);
                              const qty = parseFloat(currentItem.quantity || 1);
                              // Apply divide_by_quantity logic if needed
                              if (currentItem.nested_calculation.divide_by_quantity && qty > 0) {
                                nestedAmount = nestedAmount / qty;
                              } else if (currentItem.divide_by_quantity && qty > 0) {
                                nestedAmount = nestedAmount / qty;
                              }
                              return nestedAmount * qty;
                            })()
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={(() => {
                              // Get current value from items state, not from item prop
                              const currentItem = items.find(i => i.row_order === item.row_order);
                              return currentItem?.nested_calculation?.divide_by_quantity || false;
                            })()}
                            onChange={(e) => {
                              let prevItems = [...items];
                              const currentItem = prevItems.find(i => i.row_order === item.row_order);
                              if (!currentItem || !currentItem.nested_calculation) return;
                              
                              const updatedNestedCalc = {
                                ...currentItem.nested_calculation,
                                divide_by_quantity: e.target.checked,
                              };
                              prevItems[item.row_order] = {
                                ...currentItem,
                                nested_calculation: updatedNestedCalc,
                              };
                              setItems(prevItems);
                              const mappedItems = prevItems.map(item => ({
                                ...item,
                                item_type: item.item_type || item.type,
                                // Ensure nested_calculation with divide_by_quantity is included
                                nested_calculation: item.nested_calculation ? {
                                  ...item.nested_calculation,
                                  divide_by_quantity: item.nested_calculation.divide_by_quantity || false,
                                } : null,
                              }));
                              setFieldValue(name, mappedItems);
                            }}
                            size="small"
                          />
                        }
                        label="Del på mengde"
                        className="text-xs"
                      />
                    </div>
                  </div>
                </div>
              );
            })()}
            {(() => {
              // Get current item from items state
              const currentItem = items.find(i => i.row_order === item.row_order);
              return currentItem?.products?.map((prod, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium">{prod.displayTitle}</span>
                    <TrashIcon
                      className="cursor-pointer h-4 w-4 text-red-400"
                      onClick={() => handleRemoveProduct(item, prod.id)}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="grid grid-cols-12 gap-2 flex-1">
                      <div className="col-span-6 sm:col-span-2">
                        <TextField
                          label={t("offer.calculation.workspace.col.quantity")}
                          variant="standard"
                          type="number"
                          size="small"
                          fullWidth
                          value={prod.quantity || 1}
                          onChange={(e) => handleProductFieldChange(item, prod.id, "quantity", parseFloat(e.target.value) || 1)}
                        />
                      </div>
                      <div className="col-span-6 sm:col-span-2">
                        <TextField
                          label={t("offer.calculation.workspace.col.sales_price")}
                          variant="standard"
                          type="number"
                          size="small"
                          fullWidth
                          value={prod.sales_price || 0}
                          onChange={(e) => handleProductFieldChange(item, prod.id, "sales_price", parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="col-span-6 sm:col-span-2">
                        <TextField
                          label={t("offer.calculation.workspace.col.cost_price")}
                          variant="standard"
                          type="number"
                          size="small"
                          fullWidth
                          value={prod.purchase_price || 0}
                          onChange={(e) => handleProductFieldChange(item, prod.id, "purchase_price", parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="col-span-6 sm:col-span-2">
                        <TextField
                          label="Rabatt %"
                          variant="standard"
                          type="number"
                          size="small"
                          fullWidth
                          value={prod.discount || 0}
                          onChange={(e) => handleProductFieldChange(item, prod.id, "discount", parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="col-span-6 sm:col-span-2">
                        <TextField
                          label="Påslag %"
                          variant="standard"
                          type="number"
                          size="small"
                          fullWidth
                          value={prod.surcharge || 0}
                          onChange={(e) => handleProductFieldChange(item, prod.id, "surcharge", parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="col-span-6 sm:col-span-2">
                        <div className="text-right">
                          <div className="text-xs text-gray-500">Total:</div>
                          <div className="text-sm font-semibold">
                            {(() => {
                              // Get current values from items state
                              const currentItem = items.find(i => i.row_order === item.row_order);
                              const currentProd = currentItem?.products?.find(p => p.id === prod.id);
                              if (!currentProd) return priceFormatter.format(0);
                              
                              const qty = parseFloat(currentProd.quantity || 1);
                              let price = parseFloat(currentProd.sales_price || 0);
                              // Only divide products if product-level divide_by_quantity is set
                              // Item-level divide_by_quantity only affects hours and nested calculations, not products
                              if (currentProd.divide_by_quantity && currentItem.quantity && parseFloat(currentItem.quantity) > 0) {
                                price = price / parseFloat(currentItem.quantity);
                              }
                              const disc = parseFloat(currentProd.discount || 0);
                              const sur = parseFloat(currentProd.surcharge || 0);
                              let total = price * qty;
                              if (sur > 0) total += (total * sur) / 100;
                              if (disc > 0) total -= (total * disc) / 100;
                              return priceFormatter.format(total);
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex flex-col gap-2">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={(() => {
                              // Get current value from items state, not from prod prop
                              const currentItem = items.find(i => i.row_order === item.row_order);
                              const currentProd = currentItem?.products?.find(p => p.id === prod.id);
                              return currentProd?.divide_by_quantity || false;
                            })()}
                            onChange={(e) => handleProductFieldChange(item, prod.id, "divide_by_quantity", e.target.checked)}
                            size="small"
                          />
                        }
                        label="Del på mengde"
                        className="text-xs"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={(() => {
                              // Get current value from items state
                              const currentItem = items.find(i => i.row_order === item.row_order);
                              const currentProd = currentItem?.products?.find(p => p.id === prod.id);
                              return currentProd?.show_image || false;
                            })()}
                            onChange={(e) => handleProductFieldChange(item, prod.id, "show_image", e.target.checked)}
                            size="small"
                          />
                        }
                        label="Vis bilde i PDF"
                        className="text-xs"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={(() => {
                              // Get current value from items state
                              const currentItem = items.find(i => i.row_order === item.row_order);
                              const currentProd = currentItem?.products?.find(p => p.id === prod.id);
                              return currentProd?.show_url || false;
                            })()}
                            onChange={(e) => handleProductFieldChange(item, prod.id, "show_url", e.target.checked)}
                            size="small"
                          />
                        }
                        label="Vis URL i PDF"
                        className="text-xs"
                      />
                    </div>
                  </div>
              </div>
            ));
            })()}
            <div className="flex gap-2">
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSelectedItemForCalculationSelect(item);
                  setCalculationSelectorOpen(true);
                }}
                startIcon={<PlusIcon className="h-4 w-4" />}
              >
                Legg til kalkyle
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSelectedItemForCalculation(item);
                  handleRowProductSelect(item);
                }}
                startIcon={<PlusIcon className="h-4 w-4" />}
              >
                Legg til produkt
              </Button>
            </div>
            {/* Product list authoring lives in Edit calc → Produkter og felter */}
          </div>
          </div>
        )}

        {/* Notes Section */}
        {item?.notes && item.notes.length > 0 && item?.line_type !== "note" && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700 mb-3">
              {t("offer.calculation.line_editor.notes_section")}
            </div>
            <div className="space-y-2">
              {item.notes.map((note, noteIdx) => (
                <div key={noteIdx} className="p-3 bg-gray-50 rounded border border-gray-200">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      {note.title && (
                        <div className="text-sm font-semibold text-gray-900 mb-1">{note.title}</div>
                      )}
                      {note.template_title && (
                        <div className="text-xs text-gray-500 mb-1">
                          {t("offer.calculation.line_editor.note_from_template", {
                            title: note.template_title,
                          })}
                        </div>
                      )}
                      {!isBlankNoteHtml(noteBodyHtml(note)) ? (
                        <PromangRichTextContent
                          html={noteBodyHtml(note)}
                          className="text-sm text-gray-700"
                        />
                      ) : null}
                      {note.show_in_offer && (
                        <div className="text-xs text-blue-600 mt-2 font-medium">
                          {t("offer.calculation.line_editor.shown_in_offer")}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-2 shrink-0">
                      <PencilSquareIcon
                        className="cursor-pointer h-4 w-4 text-blue-400"
                        onClick={() => openNoteDialog(item, noteIdx)}
                      />
                      <TrashIcon
                        className="cursor-pointer h-4 w-4 text-red-400"
                        onClick={() => {
                          const current =
                            items.find((row) =>
                              sameRowOrder(row?.row_order, item.row_order)
                            ) || item;
                          const updatedNotes = [...(current.notes || [])];
                          updatedNotes.splice(noteIdx, 1);
                          const next = patchRowNotes(
                            items,
                            item.row_order,
                            updatedNotes
                          );
                          if (next) commitItems(next, { persist: true });
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Card foot: display options + note + delete */}
        <div className="offer-calc-line-card__foot">
          <div className="offer-calc-line-card__foot-opts">
            {item?.line_type === "product" && (
              <>
                <span className="offer-calc-line-card__foot-label">
                  {t("offer.calculation.line_editor.display_options")}
                </span>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item?.show_image !== false}
                      onChange={(e) => {
                        let prevItems = [...items];
                        prevItems[item.row_order] = {
                          ...prevItems[item.row_order],
                          show_image: e.target.checked,
                        };
                        setItems(prevItems);
                      }}
                      size="small"
                    />
                  }
                  label={t("offer.calculation.line_editor.show_product_image_pdf")}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item?.show_url !== false}
                      onChange={(e) => {
                        let prevItems = [...items];
                        prevItems[item.row_order] = {
                          ...prevItems[item.row_order],
                          show_url: e.target.checked,
                        };
                        setItems(prevItems);
                      }}
                      size="small"
                    />
                  }
                  label={t("offer.calculation.line_editor.show_product_url_pdf")}
                />
              </>
            )}
          </div>
          <div className="offer-calc-line-card__foot-actions">
            {item?.line_type !== "note" && (
            <button
              type="button"
              className="offer-calc-line-card__note-btn"
              onClick={() => openNoteDialog(item)}
            >
              <PlusIcon className="h-4 w-4" aria-hidden />
              {t("offer.calculation.line_editor.add_note")}
            </button>
            )}
            <button
              type="button"
              className="offer-calc-line-card__delete-btn"
              title={t("offer.calculation.line_editor.delete_row")}
              onClick={() => deleteRow(item.row_order)}
            >
              <TrashIcon className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCompactRow = (item, index, opts = {}) => {
    const depth = opts.depth || 0;
    const rowOrder =
      item.row_order !== undefined && item.row_order !== null
        ? item.row_order
        : index;
    const isSelected =
      selectedRowOrder != null &&
      Number(selectedRowOrder) === Number(rowOrder);
    const isNoteLine =
      item?.line_type === "note" || item?.item_type === "text";
    const isChecked = selectedRowOrders.has(rowOrder);
    const salesDisp = getRowSalesDisplay(item);
    const unitCost = getRowUnitCost(item);
    const unitSales = getRowUnitSales(item);
    const marginPct = getRowMarginPercent(item);
    const qty = parseFormattedNumber(item?.quantity || 0);
    const hours = parseFormattedNumber(item?.hours || 0);
    const surcharge = parseFormattedNumber(item?.surcharge || 0);
    const discount = parseFormattedNumber(item?.discount || 0);
    const unitLabel = resolveUnitLabel(item);
    const lineCostV2 = calculationV2Enabled
      ? resolveLineCostDisplay(item, { v2: true })
      : null;
    const costDisplay =
      lineCostV2 != null
        ? lineCostV2
        : unitCost > 0
        ? unitCost
        : null;
    const missingCost =
      item?.line_type !== "note" &&
      (salesDisp?.amount > 0 || unitSales > 0) &&
      !(costDisplay > 0) &&
      !(unitCost > 0);
    const canDragLine = true;
    const insertSummary =
      item?.line_type === "calculation"
        ? summarizeRecommendedInsertFields(
            item?.nested_calculation?.module_graph,
            t
          )
        : "";
    const insertNeedsAttention =
      item?.line_type === "calculation" &&
      isModuleCalculation(item?.nested_calculation) &&
      recommendedFieldsNeedAttention(
        item?.nested_calculation?.module_graph,
        t
      );
    const nestedUnitTotal = parseMoneyAmount(
      item?.nested_calculation?.total_amount ?? item?.sales_price
    );
    const qtyTimesUnitHint =
      item?.line_type === "calculation" &&
      qty > 0 &&
      nestedUnitTotal != null &&
      nestedUnitTotal > 0
        ? `${formatNumber(qty)} × ${priceFormatter.format(nestedUnitTotal)}`
        : null;
    const moduleNoteCount = (
      item?.nested_calculation?.module_graph?.nodes || []
    ).filter(
      (n) =>
        n?.type === "note" &&
        String(n?.data?.note || "").trim().length > 0
    ).length;

    return (
      <div
        className="offer-calc-grid__row-wrap"
        key={item.draggableId || index}
        style={depth > 0 ? { paddingLeft: Math.min(depth, 4) * 12 } : undefined}
      >
        <div
          data-offer-calc-row={rowOrder}
          className={[
            "offer-calc-grid__row",
            isSelected ? "is-selected" : "",
            isChecked ? "is-checked" : "",
            isNoteLine ? "is-note" : "",
            missingCost ? "is-missing-cost" : "",
            canDragLine ? "is-draggable" : "",
            insertNeedsAttention ? "is-insert-attention" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          role="row"
          tabIndex={0}
          aria-selected={isSelected}
          draggable={canDragLine}
          onDragStart={(e) => {
            if (!canDragLine) return;
            const payload = JSON.stringify({ rowOrder });
            e.dataTransfer.setData(LINE_DROP_MIME, payload);
            e.dataTransfer.setData("text/plain", payload);
            e.dataTransfer.effectAllowed = "move";
          }}
          onDragOver={(e) => {
            const types = [...e.dataTransfer.types];
            if (!types.includes(LINE_DROP_MIME) && !types.includes("text/plain")) {
              return;
            }
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = "move";
            e.currentTarget.classList.add("is-drop-target");
          }}
          onDragLeave={(e) => {
            e.currentTarget.classList.remove("is-drop-target");
          }}
          onDrop={(e) => {
            e.currentTarget.classList.remove("is-drop-target");
            e.preventDefault();
            e.stopPropagation();
            const raw =
              e.dataTransfer.getData(LINE_DROP_MIME) ||
              e.dataTransfer.getData("text/plain");
            if (!raw) return;
            try {
              const { rowOrder: fromRowOrder } = JSON.parse(raw);
              if (fromRowOrder == null) return;
              applyLineReorder(fromRowOrder, rowOrder);
            } catch {
              /* ignore */
            }
          }}
          onClick={() => onSelectRow?.(rowOrder)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSelectRow?.(rowOrder);
            }
          }}
        >
          <span
            className="offer-calc-grid__cell offer-calc-grid__cell--select"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              className="offer-calc-grid__select-box"
              checked={isChecked}
              aria-label={t("offer.calculation.workspace.bulk.select_row")}
              onClick={(e) => {
                e.stopPropagation();
                toggleRowSelection(rowOrder, index, e.shiftKey);
              }}
              onChange={() => {}}
            />
          </span>
          <span className="offer-calc-grid__cell offer-calc-grid__cell--type">
            <span
              className={`offer-calc-line-type-badge offer-calc-line-type-badge--grid is-${workspaceBadgeType(item?.line_type)}`}
              title={t(WORKSPACE_LINE_TYPE_LABEL_KEY(item?.line_type))}
            >
              {lineTypeIconEl(
                item?.line_type === "sale" ? "product" : item?.line_type,
                "h-3.5 w-3.5"
              )}
              <span className="offer-calc-grid__type-label">
                {t(WORKSPACE_LINE_TYPE_LABEL_KEY(item?.line_type))}
              </span>
            </span>
          </span>
          <span className="offer-calc-grid__cell offer-calc-grid__cell--desc">
            <span className="offer-calc-grid__desc-stack">
              <span
                className="offer-calc-grid__desc-title"
                title={insertSummary || undefined}
              >
                {lineTitle(item, index)}
              </span>
              {qtyTimesUnitHint ? (
                <span className="offer-calc-grid__desc-meta">
                  {t("offer.calculation.line.qty_times_unit", {
                    formula: qtyTimesUnitHint,
                  })}
                </span>
              ) : null}
              {moduleNoteCount > 0 ? (
                <span className="offer-calc-grid__desc-meta">
                  {t("offer.calculation.line.module_notes_count", {
                    count: moduleNoteCount,
                  })}
                </span>
              ) : null}
            </span>
            {insertNeedsAttention ? (
              <span
                className="offer-calc-grid__warn"
                title={t("offer.calculation.line.insert_needs_attention")}
              >
                ⚠
              </span>
            ) : null}
            {missingCost ? (
              <span
                className="offer-calc-grid__warn"
                title={t("offer.calculation.workspace.missing_cost_line")}
              >
                ⚠
              </span>
            ) : null}
          </span>
          {isNoteLine ? null : (
            <>
          <span className="offer-calc-grid__cell offer-calc-grid__cell--num tabular-nums">
            {qty ? formatNumber(qty) : "—"}
          </span>
          <span className="offer-calc-grid__cell offer-calc-grid__cell--unit">
            {unitLabel || "—"}
          </span>
          <span className="offer-calc-grid__cell offer-calc-grid__cell--num offer-calc-grid__cell--hours tabular-nums">
            {hours > 0 ? formatNumber(hours) : "—"}
          </span>
          <span className="offer-calc-grid__cell offer-calc-grid__cell--num offer-calc-grid__cell--cost tabular-nums">
            {costDisplay > 0 ? priceFormatter.format(costDisplay) : "—"}
          </span>
          <span className="offer-calc-grid__cell offer-calc-grid__cell--num offer-calc-grid__cell--markup tabular-nums">
            {surcharge > 0 || discount > 0
              ? [
                  surcharge > 0 ? `+${formatNumber(surcharge)} %` : null,
                  discount > 0 ? `−${formatNumber(discount)} %` : null,
                ]
                  .filter(Boolean)
                  .join(" ")
              : "—"}
          </span>
          <span className="offer-calc-grid__cell offer-calc-grid__cell--num tabular-nums">
            {unitSales > 0 ? priceFormatter.format(unitSales) : "—"}
          </span>
          <span className="offer-calc-grid__cell offer-calc-grid__cell--num tabular-nums offer-calc-grid__linesum">
            <span className="offer-calc-grid__linesum-stack">
              <span className="offer-calc-grid__linesum-label">
                {t("offer.calculation.workspace.col.line_sum")}
              </span>
              <span className="offer-calc-grid__linesum-amount">
                {salesDisp.amount != null
                  ? priceFormatter.format(salesDisp.amount)
                  : "—"}
              </span>
              {salesDisp.estimate ? (
                <span className="offer-calc-grid__flag">
                  {t("offer.calculation.totals.estimate_hint")}
                </span>
              ) : null}
            </span>
          </span>
          <span className="offer-calc-grid__cell offer-calc-grid__cell--num tabular-nums">
            {marginPct != null ? `${marginPct.toFixed(1)} %` : "—"}
          </span>
          {colRisiko ? (
            <span className="offer-calc-grid__cell offer-calc-grid__cell--num offer-calc-grid__cell--optional tabular-nums">
              {item?.risk != null && item?.risk !== ""
                ? String(item.risk)
                : "—"}
            </span>
          ) : null}
          {colKilde ? (
            <span className="offer-calc-grid__cell offer-calc-grid__cell--optional">
              {item?.price_source || "—"}
            </span>
          ) : null}
            </>
          )}
          <span
            className="offer-calc-grid__cell offer-calc-grid__cell--actions"
            onClick={(e) => e.stopPropagation()}
          >
            {showGroupHierarchy ? (
              <button
                type="button"
                className="offer-calc-grid__row-move"
                onClick={(e) => openMoveMenu(e, rowOrder)}
                title={t("offers.groups.move_line")}
                aria-label={t("offers.groups.move_line")}
              >
                <FolderOutlineIcon className="h-4 w-4" />
              </button>
            ) : null}
            <button
              type="button"
              className="offer-calc-grid__row-del"
              onClick={(e) => {
                e.stopPropagation();
                deleteRow(item.row_order);
              }}
              aria-label={t("offer.calculation.delete")}
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </span>
        </div>
        {item?.line_type === "calculation" && item?.nested_calculation_id ? (
          <div
            className="offer-calc-grid__insert-strip"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <NestedModuleInsertFields
              nestedCalculation={
                item.nested_calculation?.id
                  ? item.nested_calculation
                  : {
                      id: item.nested_calculation_id,
                      ...(item.nested_calculation || {}),
                    }
              }
              onLocalChange={(nextNested) => patchNestedOnItem(item, nextNested)}
              onOpenBuilder={
                onEditNestedCalculation
                  ? (opts) =>
                      onEditNestedCalculation(
                        item.nested_calculation?.id
                          ? item.nested_calculation
                          : { id: item.nested_calculation_id },
                        opts
                      )
                  : undefined
              }
            />
          </div>
        ) : null}
        {isSelected ? (
          <div
            className="offer-calc-grid__editor"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {renderItemRow(item, index)}
          </div>
        ) : null}
      </div>
    );
  };

  const groupHeaderSubtotal = (groupId) => {
    const key = groupId == null ? null : String(groupId);
    let sales = 0;
    (items || []).forEach((item) => {
      const gid = readOfferGroupId(item);
      if (key == null) {
        if (gid != null) return;
      } else if (gid == null || String(gid) !== key) {
        return;
      }
      const salesDisp = getRowSalesDisplay(item);
      if (salesDisp?.amount != null) sales += Number(salesDisp.amount) || 0;
    });
    return sales;
  };

  const renderGroupHeaderRow = (displayRow) => {
    const isUngrouped = displayRow.kind === "ungrouped";
    const group = displayRow.group;
    const idKey = isUngrouped
      ? UNGROUPED_KEY
      : String(group.id ?? group.clientId);
    const collapsed = collapsedGridGroups.has(idKey);
    const selected =
      !isUngrouped &&
      selectedGroupId != null &&
      String(selectedGroupId) === String(group.id);
    const label = isUngrouped
      ? t("offers.groups.ungrouped")
      : formatGroupLabel(
          group,
          groupDisplayTitle(group) || t("offers.groups.untitled")
        );
    const subtotal = isUngrouped
      ? groupHeaderSubtotal(null)
      : groupHeaderSubtotal(group.id);
    const depth = displayRow.depth || 0;

    return (
      <div
        key={`ghdr-${idKey}`}
        data-offer-calc-group={isUngrouped ? UNGROUPED_KEY : group.id}
        className={[
          "offer-calc-grid__group-header",
          selected ? "is-selected" : "",
          collapsed ? "is-collapsed" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ paddingLeft: 8 + depth * 12 }}
        role="row"
        onDragOver={(e) => {
          const types = [...e.dataTransfer.types];
          if (!types.includes(LINE_DROP_MIME) && !types.includes("text/plain")) {
            return;
          }
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          e.currentTarget.classList.add("is-drop-target");
        }}
        onDragLeave={(e) => {
          e.currentTarget.classList.remove("is-drop-target");
        }}
        onDrop={(e) => {
          e.currentTarget.classList.remove("is-drop-target");
          e.preventDefault();
          const raw =
            e.dataTransfer.getData(LINE_DROP_MIME) ||
            e.dataTransfer.getData("text/plain");
          if (!raw) return;
          try {
            const { rowOrder } = JSON.parse(raw);
            if (rowOrder == null) return;
            const targetGroupId = isUngrouped ? null : group.id;
            // Local apply — grid owns items[]; do not detour via tree bridge.
            applyOfferGroupId(rowOrder, targetGroupId);
          } catch {
            /* ignore */
          }
        }}
        onClick={() => {
          if (!isUngrouped && onSelectGroup) {
            onSelectGroup(group);
          }
        }}
      >
        <button
          type="button"
          className="offer-calc-grid__group-caret"
          onClick={(e) => {
            e.stopPropagation();
            toggleGridGroupCollapse(idKey);
          }}
          aria-expanded={!collapsed}
        >
          {collapsed ? "▸" : "▾"}
        </button>
        <FolderIcon
          className="offer-calc-grid__group-folder"
          aria-hidden
        />
        {!isUngrouped && group.number ? (
          <span className="offer-calc-grid__group-number" aria-hidden>
            {group.number}
          </span>
        ) : null}
        <span className="offer-calc-grid__group-title">{label}</span>
        <span className="offer-calc-grid__group-count">
          {displayRow.itemCount}
        </span>
        <span className="offer-calc-grid__group-subtotal tabular-nums">
          {subtotal > 0 ? priceFormatter.format(subtotal) : "—"}
        </span>
      </div>
    );
  };

  const renderTotalCell = (label, value, opts = {}) => (
    <div
      className={[
        "offer-calc-grid__total",
        opts.emphasis ? "is-emphasis" : "",
        opts.positive ? "is-positive" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span className="offer-calc-grid__total-label">
        <span className="inline-flex items-center gap-0.5">
          {label}
          {opts.infoKey ? (
            <CalculationInfoIcon
              ariaLabel={t("offer.calculation.summary.info_aria", {
                label,
              })}
              onClick={() => setSummaryGuideKey(opts.infoKey)}
            />
          ) : null}
        </span>
        {opts.estimate ? (
          <span className="offer-calc-grid__total-flag is-estimate">
            {t("offer.calculation.totals.estimate_hint")}
          </span>
        ) : opts.authoritative ? (
          <span className="offer-calc-grid__total-flag">
            {t("offer.calculation.totals.authoritative_hint")}
          </span>
        ) : null}
      </span>
      <span className="offer-calc-grid__total-value tabular-nums">{value}</span>
    </div>
  );


  const renderCompactWorkspace = () => {
    const totals = getWorkspaceTotals();
    const hasItems = items && items.length > 0;
    const rowHeight =
      gridDensity === "comfortable"
        ? OFFER_CALC_COMFORTABLE_ROW_HEIGHT
        : OFFER_CALC_COMPACT_ROW_HEIGHT;
    const groupedDisplayRows = showGroupHierarchy
      ? buildGroupedDisplayRows(flatHierarchyGroups, items, collapsedGridGroups)
      : null;
    // offer-17: virtualize only large lists AND only when no inline editor is
    // expanded (an expanded row has variable height and must not be virtualized).
    // Group headers also have variable structure — skip virtualization then.
    const hasInlineInsertFields = (items || []).some(
      (row) =>
        row?.line_type === "calculation" &&
        row?.nested_calculation_id &&
        (row.nested_calculation?.module_enabled == null ||
          isModuleCalculation(row.nested_calculation))
    );
    const useVirtual =
      hasItems &&
      !showGroupHierarchy &&
      items.length > OFFER_CALC_VIRTUALIZE_THRESHOLD &&
      selectedRowOrder == null &&
      !hasInlineInsertFields;
    const virtualHeight = Math.max(gridBodyHeight, rowHeight);
    const missingCount = totals.missingCostRows?.length || 0;
    const fmtOrDash = (value) =>
      value != null ? priceFormatter.format(value) : "—";
    const dgText =
      totals.marginPercent != null
        ? `${Number(totals.marginPercent).toFixed(1)} %`
        : "—";

    return (
      <div
        className={[
          "offer-calc-grid",
          gridDensity === "comfortable" ? "is-density-comfortable" : "is-density-compact",
          colRisiko ? "has-col-risiko" : "",
          colKilde ? "has-col-kilde" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="offer-calc-grid__toolbar">
          <div className="offer-calc-grid__toolbar-left">
            <div className="offer-calc-grid__settings-wrap" ref={settingsMenuRef}>
              <button
                type="button"
                className={[
                  "offer-calc-grid__tool-btn",
                  "offer-calc-grid__settings-btn",
                  settingsMenuOpen ? "is-open" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setSettingsMenuOpen((open) => !open)}
                aria-expanded={settingsMenuOpen}
                aria-haspopup="dialog"
                aria-controls="offer-calc-grid-settings-menu"
                aria-label={t("offer.calculation.workspace.settings_aria")}
                title={t("offer.calculation.workspace.settings")}
              >
                <Cog6ToothIcon className="h-4 w-4" aria-hidden="true" />
              </button>
              {settingsMenuOpen ? (
                <div
                  id="offer-calc-grid-settings-menu"
                  className="offer-calc-grid__settings-menu"
                  role="dialog"
                  aria-label={t("offer.calculation.workspace.settings")}
                >
                  <div className="offer-calc-grid__settings-section">
                    <div
                      className="offer-calc-grid__settings-label"
                      id="offer-calc-grid-density-label"
                    >
                      {t("offer.calculation.workspace.density")}
                    </div>
                    <div
                      className="offer-calc-grid__density"
                      role="group"
                      aria-labelledby="offer-calc-grid-density-label"
                    >
                      <button
                        type="button"
                        className={[
                          "offer-calc-grid__density-btn",
                          gridDensity === "compact" ? "is-active" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        aria-pressed={gridDensity === "compact"}
                        onClick={() => setGridDensity("compact")}
                      >
                        {t("offer.calculation.workspace.density.compact")}
                      </button>
                      <button
                        type="button"
                        className={[
                          "offer-calc-grid__density-btn",
                          gridDensity === "comfortable" ? "is-active" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        aria-pressed={gridDensity === "comfortable"}
                        onClick={() => setGridDensity("comfortable")}
                      >
                        {t("offer.calculation.workspace.density.comfortable")}
                      </button>
                    </div>
                  </div>

                  <div className="offer-calc-grid__settings-section">
                    <div
                      className="offer-calc-grid__settings-label"
                      id="offer-calc-grid-columns-label"
                    >
                      {t("offer.calculation.workspace.columns")}
                    </div>
                    <div
                      className="offer-calc-grid__settings-columns"
                      role="group"
                      aria-labelledby="offer-calc-grid-columns-label"
                    >
                      <label className="offer-calc-grid__col-menu-item">
                        <input
                          type="checkbox"
                          checked={colRisiko}
                          onChange={() => setColRisiko((v) => !v)}
                        />
                        {t("offer.calculation.workspace.col.risk")}
                      </label>
                      <label className="offer-calc-grid__col-menu-item">
                        <input
                          type="checkbox"
                          checked={colKilde}
                          onChange={() => setColKilde((v) => !v)}
                        />
                        {t("offer.calculation.workspace.col.source")}
                      </label>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <button
              type="button"
              className="offer-calc-grid__tool-btn"
              onClick={() => {
                if (typeof onOpenPresentation === "function") {
                  onOpenPresentation();
                }
              }}
            >
              {t("offers.presentation.open_button")}
            </button>

            {typeof onAddStructure === "function" ? (
              <button
                type="button"
                className="offer-calc-grid__tool-btn"
                onClick={() => onAddStructure()}
                title={t("offers.groups.add_top")}
              >
                ＋ {t("offer.calculation.workspace.add_structure")}
              </button>
            ) : null}

            <button
              type="button"
              className="offer-calc-grid__tool-btn"
              onClick={() => {
                if (typeof onLoadPageTemplate === "function") {
                  onLoadPageTemplate();
                  return;
                }
                toast(t("offer.calculation.workspace.use_template_stub"));
              }}
            >
              {t("offer.calculation.workspace.load_page_template")}
            </button>

            <button
              type="button"
              className="offer-calc-grid__tool-btn"
              onClick={() => {
                if (typeof onUseTemplate === "function") {
                  onUseTemplate();
                  return;
                }
                toast(t("offer.calculation.workspace.use_template_stub"));
              }}
            >
              ＋ {t("offer.calculation.workspace.use_template")}
            </button>

            <div className="offer-calc-grid__add">
              <button
                type="button"
                className="offer-calc-grid__add-btn is-primary"
                onClick={() => addItemRow("manual")}
              >
                <PlusIcon className="h-4 w-4" />
                {t("offer.calculation.workspace.line_type.manual")}
              </button>
              <button
                type="button"
                className="offer-calc-grid__add-btn"
                onClick={() => {
                  if (typeof onUseTemplate === "function") {
                    onUseTemplate();
                    return;
                  }
                  addItemRow("calculation");
                }}
              >
                <PlusIcon className="h-4 w-4" />
                {t("offer.calculation.workspace.line_type.calculation")}
              </button>
              <button
                type="button"
                className="offer-calc-grid__add-btn"
                onClick={() => addItemRow("hours")}
              >
                <PlusIcon className="h-4 w-4" />
                {t("offer.calculation.workspace.line_type.hours")}
              </button>
              <button
                type="button"
                className="offer-calc-grid__add-btn"
                onClick={() => addItemRow("product")}
              >
                <PlusIcon className="h-4 w-4" />
                {t("offer.calculation.workspace.line_type.product")}
              </button>
              <button
                type="button"
                className="offer-calc-grid__add-btn"
                onClick={() => addItemRow("note")}
              >
                <PlusIcon className="h-4 w-4" />
                {t("offer.calculation.workspace.line_type.note")}
              </button>
            </div>
          </div>

          <span
            className={[
              "offer-calc-grid__save-status",
              saveStatus === "pending" || saveStatus === "saving"
                ? "is-busy"
                : "",
              saveStatus === "saved" ? "is-saved" : "",
              saveStatus === "error" ? "is-error" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            role="status"
            aria-live="polite"
          >
            <span
              className={[
                "offer-calc-grid__save-dot",
                saveStatus === "saving" || saveStatus === "pending"
                  ? "is-saving"
                  : saveStatus === "saved"
                    ? "is-saved"
                    : saveStatus === "error"
                      ? "is-error"
                      : "is-idle",
              ].join(" ")}
              aria-hidden
            />
            {t(`offer.calculation.workspace.save_status.${saveStatus}`, {
              defaultValue: t("offer.calculation.workspace.save_status.idle"),
            })}
          </span>
        </div>

        {hasItems || showGroupHierarchy ? (
          <div className="offer-calc-grid__table" role="table">
            <div className="offer-calc-grid__head" role="row">
              <span className="offer-calc-grid__cell offer-calc-grid__cell--select">
                <input
                  type="checkbox"
                  className="offer-calc-grid__select-box"
                  checked={allRowsSelected}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate =
                        selectedRowOrders.size > 0 && !allRowsSelected;
                    }
                  }}
                  aria-label={t("offer.calculation.workspace.bulk.select_all")}
                  onChange={toggleSelectAll}
                />
              </span>
              <span className="offer-calc-grid__cell offer-calc-grid__cell--type">
                {t("offer.calculation.workspace.col.type")}
              </span>
              <span className="offer-calc-grid__cell offer-calc-grid__cell--desc">
                {t("offer.calculation.workspace.col.description")}
              </span>
              <span className="offer-calc-grid__cell offer-calc-grid__cell--num">
                {t("offer.calculation.workspace.col.quantity")}
              </span>
              <span className="offer-calc-grid__cell offer-calc-grid__cell--unit">
                {t("offer.calculation.workspace.col.unit_short")}
              </span>
              <span className="offer-calc-grid__cell offer-calc-grid__cell--num offer-calc-grid__cell--hours">
                {t("offer.calculation.workspace.col.hours")}
              </span>
              <span className="offer-calc-grid__cell offer-calc-grid__cell--num offer-calc-grid__cell--cost">
                {t("offer.calculation.workspace.col.cost")}
              </span>
              <span className="offer-calc-grid__cell offer-calc-grid__cell--num offer-calc-grid__cell--markup">
                {t("offer.calculation.workspace.col.markup")}
              </span>
              <span className="offer-calc-grid__cell offer-calc-grid__cell--num">
                {t("offer.calculation.workspace.col.sales_price")}
              </span>
              <span className="offer-calc-grid__cell offer-calc-grid__cell--num">
                {t("offer.calculation.workspace.col.total")}
              </span>
              <span className="offer-calc-grid__cell offer-calc-grid__cell--num">
                {t("offer.calculation.workspace.col.margin")}
              </span>
              {colRisiko ? (
                <span className="offer-calc-grid__cell offer-calc-grid__cell--num offer-calc-grid__cell--optional">
                  {t("offer.calculation.workspace.col.risk")}
                </span>
              ) : null}
              {colKilde ? (
                <span className="offer-calc-grid__cell offer-calc-grid__cell--optional">
                  {t("offer.calculation.workspace.col.source")}
                </span>
              ) : null}
              <span className="offer-calc-grid__cell offer-calc-grid__cell--actions" />
            </div>
            {useVirtual ? (
              <div
                ref={setGridBodyEl}
                className="offer-calc-grid__body offer-calc-grid__body--virtual"
              >
                <FixedSizeList
                  height={virtualHeight}
                  width="100%"
                  itemCount={items.length}
                  itemSize={rowHeight}
                  overscanCount={8}
                >
                  {({ index, style }) => (
                    <div style={style} className="offer-calc-grid__vrow">
                      {renderCompactRow(items[index], index)}
                    </div>
                  )}
                </FixedSizeList>
              </div>
            ) : (
              <div ref={setGridBodyEl} className="offer-calc-grid__body">
                {groupedDisplayRows
                  ? groupedDisplayRows.map((row) => {
                      if (row.kind === "group" || row.kind === "ungrouped") {
                        return renderGroupHeaderRow(row);
                      }
                      return renderCompactRow(row.item, row.itemIndex, {
                        depth: row.depth,
                      });
                    })
                  : items.map((item, index) => renderCompactRow(item, index))}
              </div>
            )}
          </div>
        ) : (
          <div className="offer-calc-workspace__pane-empty py-8 text-center">
            <p className="offer-calc-workspace__pane-empty-title">
              {t("offer.calculation.workspace.lines_empty_title")}
            </p>
            <p className="offer-calc-workspace__pane-empty-hint mt-1 text-sm text-slate-500">
              {t("offer.calculation.workspace.lines_empty_hint")}
            </p>
          </div>
        )}

        {showGroupHierarchy ? (
          <Menu
            anchorEl={moveMenu.anchorEl}
            open={Boolean(moveMenu.anchorEl)}
            onClose={closeMoveMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem disabled dense>
              {t("offers.groups.move_line")}
            </MenuItem>
            <MenuItem dense onClick={() => handleMoveMenuSelect(null)}>
              {t("offers.groups.ungrouped")}
            </MenuItem>
            {flatHierarchyGroups.map((g) => (
              <MenuItem
                key={g.id}
                dense
                onClick={() => handleMoveMenuSelect(g.id)}
              >
                {formatGroupLabel(
                  g,
                  groupDisplayTitle(g) || t("offers.groups.untitled")
                )}
              </MenuItem>
            ))}
          </Menu>
        ) : null}

        {totalsOpen ? (
          <OfferViewportSummary>
          <div
            className="offer-calc-grid__totals offer-calc-grid__totals--claude"
            role="status"
            aria-live="polite"
          >
            <div className="offer-calc-grid__totals-group">
              {renderTotalCell(
                t("offer.calculation.workspace.totals.material"),
                fmtOrDash(totals.materialCost)
              )}
              {renderTotalCell(
                t("offer.calculation.workspace.totals.labor"),
                fmtOrDash(totals.laborCost)
              )}
              {renderTotalCell(
                t("offer.calculation.workspace.totals.module"),
                fmtOrDash(totals.moduleCost)
              )}
              {renderTotalCell(
                t("offer.calculation.workspace.totals.hours_sum"),
                totals.hoursSum != null
                  ? formatNumber(totals.hoursSum)
                  : "—",
                { infoKey: "hours" }
              )}
            </div>
            <div className="offer-calc-grid__totals-sep" aria-hidden />
            <div className="offer-calc-grid__totals-group">
              {renderTotalCell(
                t("offer.calculation.workspace.totals.total_cost"),
                totals.cost != null ? priceFormatter.format(totals.cost) : "—",
                { estimate: totals.costEstimate, infoKey: "cost" }
              )}
              {renderTotalCell(
                t("offer.calculation.workspace.totals.contribution"),
                totals.margin != null
                  ? priceFormatter.format(totals.margin)
                  : "—",
                {
                  estimate: totals.legacyEstimate,
                  positive: totals.margin != null && totals.margin >= 0,
                  infoKey: "db",
                }
              )}
              {renderTotalCell(
                t("offer.calculation.workspace.totals.margin_rate"),
                dgText,
                {
                  estimate: totals.legacyEstimate,
                  positive:
                    totals.marginPercent != null && totals.marginPercent >= 0,
                  infoKey: "dg",
                }
              )}
            </div>
            <div className="offer-calc-grid__totals-sep" aria-hidden />
            {renderTotalCell(
              t("offer.calculation.workspace.totals.offer_ex_vat"),
              priceFormatter.format(totals.sales ?? 0),
              {
                emphasis: true,
                estimate: totals.salesEstimate,
                authoritative: totals.salesAuthoritative,
              }
            )}
            {missingCount > 0 ? (
              <button
                type="button"
                className="offer-calc-grid__missing-chip"
                onClick={() => {
                  const first = totals.missingCostRows[0];
                  if (first == null) return;
                  onSelectRow?.(first);
                  requestAnimationFrame(() => {
                    document
                      .querySelector(`[data-offer-calc-row="${first}"]`)
                      ?.scrollIntoView?.({
                        behavior: "smooth",
                        block: "center",
                      });
                  });
                }}
              >
                ⚠{" "}
                {t("offer.calculation.workspace.missing_cost", {
                  count: missingCount,
                })}
              </button>
            ) : null}
            <button
              type="button"
              className="offer-calc-grid__totals-toggle"
              onClick={() => setTotalsOpen(false)}
              aria-label={t("offer.calculation.workspace.totals.collapse")}
            >
              ▾
            </button>
          </div>
          </OfferViewportSummary>
        ) : (
          <OfferViewportSummary>
          <div className="offer-calc-grid__totals offer-calc-grid__totals--compact">
            <span className="offer-calc-grid__totals-compact-label">
              {t("offer.calculation.workspace.totals.compact", {
                sales: priceFormatter.format(totals.sales ?? 0),
                dg: dgText,
              })}
            </span>
            <button
              type="button"
              className="offer-calc-grid__totals-toggle"
              onClick={() => setTotalsOpen(true)}
              aria-label={t("offer.calculation.workspace.totals.expand")}
            >
              ▴
            </button>
          </div>
          </OfferViewportSummary>
        )}

        <BulkBar
          count={selectedRowOrders.size}
          onClear={clearSelection}
          actions={[
            {
              label: t("offer.calculation.workspace.bulk.delete_selected"),
              icon: TrashIcon,
              action: () => setBulkDeleteConfirmOpen(true),
              danger: true,
            },
          ]}
        />
      </div>
    );
  };

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <div
      className={workspaceMode ? "flex min-h-0 flex-1 flex-col" : undefined}
      onFocusCapture={selectCalcNumericFieldOnFocus}
      onMouseUpCapture={keepCalcNumericSelectionOnMouseUp}
    >
      {!workspaceMode ? (
        <div className="mb-4 flex justify-end">
          <Button
            variant="outlined"
            size="small"
            onClick={() => setSettingsDialogOpen(true)}
          >
            {t("offer.calculation.workspace.display_settings")}
          </Button>
        </div>
      ) : null}

      {workspaceMode ? (
        renderCompactWorkspace()
      ) : (
      <div>
        {(() => {
          if (items && items.length > 0) {
            return items.map((item, index) => renderItemRow(item, index));
          } else {
            return (
              <div className="offer-calc-workspace__pane-empty py-8 text-center">
                <p className="offer-calc-workspace__pane-empty-title">
                  {t("offer.calculation.workspace.lines_empty_title")}
                </p>
                <p className="offer-calc-workspace__pane-empty-hint mt-1 text-sm text-slate-500">
                  {t("offer.calculation.workspace.lines_empty_hint")}
                </p>
              </div>
            );
          }
        })()}

        <div className="mt-4 flex justify-between items-center">
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => addItemRow("manual")} variant="contained">
              <PlusCircleIcon className="h-5 w-5 mr-2" />
              Ny manuell linje
            </Button>
            <Button onClick={() => addItemRow("calculation")} variant="outlined">
              <PlusCircleIcon className="h-5 w-5 mr-2" />
              Ny beregningslinje
            </Button>
            <Button onClick={() => addItemRow("hours")} variant="outlined">
              <PlusCircleIcon className="h-5 w-5 mr-2" />
              Ny timelinje
            </Button>
            <Button onClick={() => addItemRow("product")} variant="outlined">
              <PlusCircleIcon className="h-5 w-5 mr-2" />
              Ny produktlinje
            </Button>
            <Button onClick={() => addItemRow("note")} variant="outlined">
              <PlusCircleIcon className="h-5 w-5 mr-2" />
              {t("offer.calculation.workspace.line_type.note")}
            </Button>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">
              {t("offer.calculation.totals.sales_total")}
              {getFooterSalesDisplay().estimate ? (
                <span className="ml-1 text-xs font-normal text-amber-700">
                  ({t("offer.calculation.totals.estimate_hint")})
                </span>
              ) : calculationV2Enabled && getFooterSalesDisplay().authoritative ? (
                <span className="ml-1 text-xs font-normal text-slate-400">
                  ({t("offer.calculation.totals.authoritative_hint")})
                </span>
              ) : null}
            </div>
            <div className="text-2xl font-bold">
              {priceFormatter.format(getFooterSalesDisplay().amount)}
            </div>
          </div>
        </div>
      </div>
      )}

      <ItemSelectorV2
        multiple={selectedItemForCalculation ? true : false}
        open={productSelectorOpen}
        onClose={() => {
          setProductSelectorOpen(false);
          setSelectedItemForCalculation(null);
        }}
        selectedItems={
          selectedItemForCalculation
            ? selectedItemRow?.products || []
            : selectedItemRow?.product
              ? [selectedItemRow.product]
              : []
        }
        onChange={(data) => handleProductSelect(data)}
      />

      <Dialog
        open={calculationSelectorOpen}
        onClose={() => {
          setCalculationSelectorOpen(false);
          setSelectedItemForCalculationSelect(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            height: "80vh",
            maxHeight: "600px",
          },
        }}
      >
        <ModernCalculationSelector
          open={calculationSelectorOpen}
          excludeCalculationId={calculationId}
          createTemplatePath={null}
          selectedCalculations={items
            .filter((item) => item.nested_calculation)
            .map((item) => item.nested_calculation)}
          onChange={(selectedCalculations) => {
            if (selectedItemForCalculationSelect && selectedCalculations && selectedCalculations.length > 0) {
              const selectedCalc = selectedCalculations[0];
              let prevItems = [...items];
              let item = prevItems[selectedItemForCalculationSelect.row_order];

              // Hours lines: nestede kalkyler lagres kun som notat
              if (item?.line_type === "hours") {
                const title =
                  selectedCalc.title ||
                  selectedCalc.name ||
                  `Kalkyle #${selectedCalc.id}`;
                const notes = [...(item.notes || [])];
                const already = notes.some(
                  (n) =>
                    n.source_type === "calculation" &&
                    String(n.source_id) === String(selectedCalc.id)
                );
                if (!already) {
                  notes.push({
                    title,
                    text: title,
                    show_in_offer: false,
                    source_type: "calculation",
                    source_id: selectedCalc.id,
                  });
                }
                prevItems[selectedItemForCalculationSelect.row_order] = {
                  ...item,
                  notes,
                  nested_calculation_id: null,
                  nested_calculation: null,
                };
                setItems(prevItems);
                setCalculationSelectorOpen(false);
                setSelectedItemForCalculationSelect(null);
                return;
              }

              // Never link a firm template id as nested_calculation_id —
              // that opens/edits the template instead of an offer copy.
              if (typeof onAttachNestedTemplate === "function") {
                onAttachNestedTemplate(
                  selectedItemForCalculationSelect,
                  selectedCalc
                );
              } else {
                toast.error(t("offers.tabs.calculation_load_failed"));
              }
              setCalculationSelectorOpen(false);
              setSelectedItemForCalculationSelect(null);
            }
          }}
          onClose={() => {
            setCalculationSelectorOpen(false);
            setSelectedItemForCalculationSelect(null);
          }}
          multiple={false}
          showHeader={true}
        />
      </Dialog>

      {/* Note Dialog — PromangRichTextEditor with built-in templates */}
      <Dialog
        open={noteDialogOpen}
        onClose={resetNoteDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingNoteIndex !== null
            ? t("offer.calculation.line_editor.edit_note")
            : t("offer.calculation.line_editor.add_note")}
        </DialogTitle>
        <DialogContent>
          <div className="space-y-4 mt-2">
            <TextField
              label={t("offer.calculation.line_editor.note_title")}
              variant="standard"
              fullWidth
              value={noteTitle}
              onChange={(e) => setNoteTitle(e.target.value)}
              placeholder={t("offer.calculation.line_editor.note_title_placeholder")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={noteUseTemplateTitle}
                  onChange={(e) =>
                    handleNoteUseTemplateTitleChange(e.target.checked)
                  }
                />
              }
              label={t("offer.calculation.line_editor.note_use_template_title")}
            />
            <PromangRichTextEditor
              value={noteText}
              onChange={setNoteText}
              placeholder={t("offer.calculation.line_editor.note_placeholder")}
              size="md"
              templatesEnabled
              templateButtonLabel={t("offer.calculation.line_editor.note_template_button")}
              onTemplateSelect={handleNoteTemplateSelect}
              tablesEnabled
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={noteShowInOffer}
                  onChange={(e) => setNoteShowInOffer(e.target.checked)}
                />
              }
              label={t("offer.calculation.line_editor.show_in_offer")}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetNoteDialog}>{t("Cancel")}</Button>
          <Button
            onClick={() => {
              const hasBody = !isBlankNoteHtml(noteText);
              const hasTitle = Boolean((noteTitle || "").trim());
              if (!selectedItemForNote || (!hasBody && !hasTitle)) return;

              let prevItems = [...items];
              const rowOrder = selectedItemForNote.row_order;
              const idx = findItemIndexByRowOrder(prevItems, rowOrder);
              if (idx < 0) return;
              const existing = prevItems[idx]?.notes?.[editingNoteIndex] || {};
              const currentNotes = [...(prevItems[idx].notes || [])];
              const body = hasBody ? noteText : "";
              const noteData = {
                ...existing,
                title: hasTitle ? noteTitle.trim() : null,
                text: body,
                description: body,
                show_in_offer: noteShowInOffer,
                template_id: noteTemplateId ?? existing.template_id ?? null,
                template_title:
                  noteTemplateTitle ?? existing.template_title ?? null,
              };

              if (editingNoteIndex !== null) {
                currentNotes[editingNoteIndex] = noteData;
              } else {
                currentNotes.push(noteData);
              }

              const next = patchRowNotes(prevItems, rowOrder, currentNotes);
              if (next) commitItems(next, { persist: true });
              resetNoteDialog();
            }}
            variant="contained"
            disabled={isBlankNoteHtml(noteText) && !(noteTitle || "").trim()}
          >
            {editingNoteIndex !== null
              ? t("offer.calculation.line_editor.update_note")
              : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hour Rate Selector Dialog */}
      <Dialog
        open={hourRateSelectorOpen}
        onClose={() => {
          setHourRateSelectorOpen(false);
          setSelectedItemForHourRate(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("offer.calculation.line_editor.select_hour_rate")}</DialogTitle>
        <DialogContent>
          <div className="space-y-2 mt-2">
            {hourRatesLoading && (!Array.isArray(hourRates) || hourRates.length === 0) ? (
              <div className="text-center text-gray-500 py-4">
                {t("Loading")}…
              </div>
            ) : Array.isArray(hourRates) && hourRates.length > 0 ? (
              hourRates.map((hourRate) => (
                <div
                  key={hourRate.id}
                  className="p-3 border border-gray-200 rounded cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    if (selectedItemForHourRate) {
                      let prevItems = [...items];
                      const hourRateValue = hourRate.hour_rate || 0;
                      const costRateValue = hourRate.cost_rate || 0;
                      
                      prevItems[selectedItemForHourRate.row_order] = {
                        ...prevItems[selectedItemForHourRate.row_order],
                        hour_rate: hourRateValue.toString(),
                        // Don't set purchase_price from cost_rate - that's for the line's cost price
                        hour_rate_id: hourRate.id, // Store reference to hour rate
                        cost_hour_rate: costRateValue.toString(), // Store cost hour rate for worst case calculation
                      };
                      
                      // Recalculate hours if auto-calculate is enabled
                      if (selectedItemForHourRate?.calculate_hours_from_sales) {
                        const salesPrice = parseFormattedNumber(selectedItemForHourRate.sales_price || 0);
                        const quantity = parseFormattedNumber(selectedItemForHourRate.quantity || 1);
                        const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                        if (hourRateValue > 0 && totalSalesPrice > 0) {
                          const calculatedHours = totalSalesPrice / hourRateValue;
                          prevItems[selectedItemForHourRate.row_order].hours = calculatedHours.toFixed(2);
                          prevItems[selectedItemForHourRate.row_order].calculated_hours_from_sales = calculatedHours;
                        }
                      }
                      
                      // Always calculate worst case hours for hours lines: (salgspris * mengde) / kostpris timesats
                      if (selectedItemForHourRate?.line_type === "hours" && costRateValue > 0) {
                        const salesPrice = parseFormattedNumber(selectedItemForHourRate.sales_price || 0);
                        const quantity = parseFormattedNumber(selectedItemForHourRate.quantity || 1);
                        const totalSalesPrice = salesPrice * quantity; // Multiply by quantity
                        if (totalSalesPrice > 0) {
                          const calculatedHours = totalSalesPrice / costRateValue;
                          prevItems[selectedItemForHourRate.row_order].calculated_hours_from_cost = calculatedHours;
                        }
                      }
                      
                      commitItemRow(prevItems, selectedItemForHourRate.row_order);
                    }
                    setHourRateSelectorOpen(false);
                    setSelectedItemForHourRate(null);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{hourRate.title}</div>
                      {hourRate.role_key ? (
                        <div className="mt-0.5 text-xs font-medium text-slate-500">
                          {t("offer.calculation.summary.role_key_label")}:{" "}
                          {t(
                            `offer.calculation.summary.role.${hourRate.role_key}`,
                            { defaultValue: hourRate.role_key }
                          )}
                        </div>
                      ) : null}
                      {hourRate.description && (
                        <div className="text-sm text-gray-500 mt-1 line-clamp-2">{hourRate.description}</div>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <div className="font-semibold text-gray-900">
                        {priceFormatter.format(hourRate.hour_rate || 0)}
                      </div>
                      {hourRate.cost_rate > 0 && (
                        <div className="text-xs text-gray-500">
                          Kost: {priceFormatter.format(hourRate.cost_rate)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-4">
                {t("offer.calculation.line_editor.no_hour_rates")}
                <br />
                <a 
                  href="/dashboard/offers/hour-rates/new" 
                  className="text-indigo-600 hover:text-indigo-800 underline"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open("/dashboard/offers/hour-rates/new", "_blank");
                  }}
                >
                  {t("offer.calculation.line_editor.create_hour_rate")}
                </a>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setHourRateSelectorOpen(false);
            setSelectedItemForHourRate(null);
          }}>
            Avbryt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Innstillinger for visning i tilbud/PDF</DialogTitle>
        <DialogContent>
          <div className="space-y-4 mt-2">
            <div className="text-sm text-gray-600 mb-4">
              Disse innstillingene styrer hva som vises i PDF og tilbudet for kunden. De påvirker ikke redigeringsvisningen.
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showHours}
                  onChange={(e) => setShowHours(e.target.checked)}
                />
              }
              label="Vis timeantall i tilbud/PDF"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showQuantities}
                  onChange={(e) => setShowQuantities(e.target.checked)}
                />
              }
              label="Vis mengder i tilbud/PDF"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showProducts}
                  onChange={(e) => setShowProducts(e.target.checked)}
                />
              }
              label="Vis produkter i tilbud/PDF"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showSetupOnly}
                  onChange={(e) => setShowSetupOnly(e.target.checked)}
                />
              }
              label="Hvis oppsett kalkyle"
            />
            <div className="text-sm text-gray-500 mt-1">
              Når aktivert, vises ikke produkter, beregninger eller timeberegning i PDF. Kun tittel, beskrivelse og delbeløp vises.
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showQuantitiesForOfferItemOnly}
                  onChange={(e) => setShowQuantitiesForOfferItemOnly(e.target.checked)}
                />
              }
              label="Hvis mengder tilbudspost"
            />
            <div className="text-sm text-gray-500 mt-1">
              Når aktivert, vises ikke antall for produkter/beregninger knyttet til kalkylen. Kun mengde for tilbudspost/kalkyle vises.
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeProductShortDescription}
                  onChange={(e) => setIncludeProductShortDescription(e.target.checked)}
                />
              }
              label="Medta kort beskrivelse produkt"
            />
            <div className="text-sm text-gray-500 mt-1">
              Når aktivert, vises kort beskrivelse (short_description) fra produktet i PDF når produkter vises med bilde eller URL.
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>
            Lukk
          </Button>
        </DialogActions>
      </Dialog>

      {/* Calculation Settings Dialog */}
      <Dialog
        open={calculationSettingsDialogOpen}
        onClose={() => {
          setCalculationSettingsDialogOpen(false);
          setSelectedItemForSettings(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Innstillinger for beregningslinje</DialogTitle>
        <DialogContent>
          <div className="space-y-4 mt-4">
            <FormControlLabel
              control={
                <Checkbox
                  checked={(() => {
                    // Get current value from items state, not from selectedItemForSettings
                    if (selectedItemForSettings) {
                      const currentItem = items.find(i => i.row_order === selectedItemForSettings.row_order);
                      return currentItem?.divide_by_quantity || false;
                    }
                    return false;
                  })()}
                  onChange={(e) => {
                    if (selectedItemForSettings) {
                      let prevItems = [...items];
                      const updatedItem = {
                        ...prevItems[selectedItemForSettings.row_order],
                        divide_by_quantity: e.target.checked,
                      };
                      prevItems[selectedItemForSettings.row_order] = updatedItem;
                      setItems(prevItems);
                      // Update selectedItemForSettings to reflect the change
                      setSelectedItemForSettings(updatedItem);
                      // Update Formik form
                      const mappedItems = prevItems.map(item => ({
                        ...item,
                        item_type: item.item_type || item.type,
                      }));
                      setFieldValue(name, mappedItems);
                    }
                  }}
                />
              }
              label="Del beregning på mengde"
            />
            <div className="text-sm text-gray-500 mt-2">
              Når aktivert, vil kalkyle-beløpet og timer deles på mengden i linjen. 
              For eksempel: Hvis mengde er 8, vil kalkyle-beløpet og timer deles på 8.
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCalculationSettingsDialogOpen(false);
            setSelectedItemForSettings(null);
          }}>
            Lukk
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Field Dialog */}
      <Dialog
        open={customFieldDialogOpen}
        onClose={() => {
          setCustomFieldDialogOpen(false);
          setEditingCustomField(null);
          setEditingItemForCustomField(null);
          setNewCustomField({ key: "", label: "", type: "text", value: "" });
        }}
      >
        <DialogTitle>
          {editingCustomField ? "Rediger custom field" : "Legg til custom field"}
        </DialogTitle>
        <DialogContent>
          <div className="space-y-4 mt-2">
            <TextField
              label="Nøkkel (key)"
              variant="standard"
              fullWidth
              value={newCustomField.key}
              onChange={(e) =>
                setNewCustomField({ ...newCustomField, key: e.target.value })
              }
              disabled={!!editingCustomField}
            />
            <TextField
              label="Label"
              variant="standard"
              fullWidth
              value={newCustomField.label}
              onChange={(e) =>
                setNewCustomField({ ...newCustomField, label: e.target.value })
              }
            />
            <FormControl variant="standard" fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={newCustomField.type}
                onChange={(e) =>
                  setNewCustomField({ ...newCustomField, type: e.target.value })
                }
                label="Type"
              >
                <MenuItem value="text">Tekst</MenuItem>
                <MenuItem value="number">Nummer</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Verdi"
              variant="standard"
              fullWidth
              type={newCustomField.type === "number" ? "number" : "text"}
              value={newCustomField.value}
              onChange={(e) =>
                setNewCustomField({ ...newCustomField, value: e.target.value })
              }
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCustomFieldDialogOpen(false);
              setEditingCustomField(null);
              setEditingItemForCustomField(null);
              setNewCustomField({ key: "", label: "", type: "text", value: "" });
            }}
          >
            Avbryt
          </Button>
          <Button onClick={handleSaveCustomField} variant="contained">
            Lagre
          </Button>
        </DialogActions>
      </Dialog>

      <OfferPricingMethodDialog
        open={Boolean(pricingDialogItem)}
        item={pricingDialogItem}
        onClose={() => setPricingDialogItem(null)}
        onApply={({ custom_fields }) => {
          if (!pricingDialogItem) return;
          const prevItems = [...items];
          const idx = pricingDialogItem.row_order;
          if (prevItems[idx]) {
            prevItems[idx] = { ...prevItems[idx], custom_fields };
            setItems(prevItems);
          }
        }}
      />

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={t("offer.calculation.line_editor.delete_confirm")}
        action="delete"
        module="offer_calculation_item"
        yesCallback={() => handleDeleteRow()}
        cancelCallback={() => {
          deleteTargetRef.current = null;
          setDeleteConfirmationOpen(false);
        }}
      />

      <ProConfirmation
        severity="confirmation"
        open={bulkDeleteConfirmOpen}
        message={t("offer.calculation.workspace.bulk.delete_confirm", {
          count: selectedRowOrders.size,
        })}
        action="delete"
        module="offer_calculation_item"
        yesCallback={() => handleDeleteSelected()}
        cancelCallback={() => setBulkDeleteConfirmOpen(false)}
      />

      <CalculationGuideDialog
        open={Boolean(summaryGuideKey)}
        onClose={() => setSummaryGuideKey(null)}
        titleKey={
          summaryGuideKey
            ? `offer.calculation.summary.guide.${summaryGuideKey}.title`
            : null
        }
        bodyKey={
          summaryGuideKey
            ? `offer.calculation.summary.guide.${summaryGuideKey}.body`
            : null
        }
      />
    </div>
  );
}


